/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { html, LitElement, render } from "lit";
import { customElement, property } from "lit/decorators.js";
import { FileReviewRequest, FirestoreReviewRequest, PubSubReviewRequest, ReviewRequest } from "./review-request";

import { Revision } from "./revision";
import { DocumentInput } from "./document-input";
import { convertEntitiesToEntityLabels, EntityLabel } from "./entity-label";
import { convertEntitiesToGroupingLabels, GroupingLabel } from "./grouping-label";
import { ClassificationLabel, convertEntitiesToClassificationLabels } from "./classification-label";
import { fetchRetry } from "./fetch-retry";
import { getTabTypeFromProcessorType, PROCESSOR_TYPES, TabType } from "./processor-types";
import { hitlKeyboardShortcuts } from "./keyboard-shortcuts";
import "./keyboard-shortcuts-dialog";
import type { KeyboardShortcutsDialog } from "./keyboard-shortcuts-dialog";

import { validateEntitiesWithRules, type ValidationContext } from "./validation";
import { globalSaveQueue, SaveQueueStatus } from "./save-queue";
// Import logging functions
import { log, logAction, sendCriticalLog } from "./logger";

// Lazy load Material Components only when needed
const loadMaterialComponents = async () => {
    const [
        { /* mwc-textfield */ },
        { /* mwc-button */ },
        { /* mwc-linear-progress */ }
    ] = await Promise.all([
        import("@material/mwc-textfield/mwc-textfield"),
        import("@material/mwc-button/mwc-button"),
        import("@material/mwc-linear-progress/mwc-linear-progress")
    ]);
};


// Type definitions for dynamically loaded components
interface Dialog extends HTMLElement {
    open: boolean;
    heading?: string;

    show(): void;

    close(): void;
}

const MINIMUM_SAVE_INTERVAL_SECONDS = 5;
const MINIMUM_SAVE_INTERVAL_MS = MINIMUM_SAVE_INTERVAL_SECONDS * 1000;
const QUARANTINE_MAX_ATTEMPTS = 3;
const QUARANTINE_RETRY_DELAY_MS = 1000;

enum NavEvent {
    BACK,
    PREVIOUS,
    NEXT,
}

@customElement("hitl-review-document")
export class HitlReviewDocument extends LitElement {
    @property({ type: Array })
    reviewRequests: ReviewRequest[] = [];

    @property({ type: Number })
    reviewRequestsIndex: number = 0;

    @property({ type: String })
    userId: string = "";

    // New property to store the rendered DocumentView element
    private documentViewElement: any = null;

    rejectReason: string = "";
    humanRevisionStateMessage: string = "";
    humanRevisionState: string = "requested";
    private lastDocumentLoadError: string = "";

    // Save queue status tracking
    @property({ type: Object })
    saveQueueStatus: SaveQueueStatus = {
        pending: 0,
        inProgress: 0,
        completed: 0,
        failed: 0,
        total: 0,
    };

    override createRenderRoot() {
        return this;
    }

    override firstUpdated() {
        // Attach a global change event listener to log field changes and update state
        this.addEventListener("change", this.handleFieldChange);

        // Initialize keyboard shortcuts with all handler functions
        hitlKeyboardShortcuts.initialize({
            sendToClearing: this.clickClearingButton.bind(this),
            sendToIrrlaeufer: this.sendToIrrlaeufer.bind(this),
            deleteCase: this.deleteProcess.bind(this),
            deleteExtractedFields: this.clearAllExtractedFields.bind(this),
            exitWithoutSaving: this.exitWithoutSaving.bind(this),
            nextFieldOrCompleteCase: this.navigateToNextFieldOrComplete.bind(this),
            clickSaveButton: this.clickSaveButton.bind(this),
            selectClassById: this.selectClassById.bind(this),
            showDigitBufferFeedback: this.showDigitBufferFeedback.bind(this),
            hideDigitBufferFeedback: this.hideDigitBufferFeedback.bind(this),
            zoomIn: this.zoomIn.bind(this),
            zoomOut: this.zoomOut.bind(this),
            zoomToFit: this.zoomToFit.bind(this),
            undo: this.undo.bind(this),
            redo: this.redo.bind(this)
        });

        // Activate keyboard shortcuts
        hitlKeyboardShortcuts.activate();

        // Set up save queue event listeners
        this.setupSaveQueueListeners();

        // start reviewing first document
        this.reviewDocument(this.reviewRequests[this.reviewRequestsIndex]);
    }

    private setupSaveQueueListeners(): void {
        // Listen for save queue status changes
        globalSaveQueue.addEventListener('status-changed', ((event: CustomEvent<SaveQueueStatus>) => {
            this.saveQueueStatus = event.detail;
            this.requestUpdate();
        }) as EventListener);

        // Listen for successful saves
        globalSaveQueue.addEventListener('save-completed', ((event: CustomEvent) => {
            const { reviewRequest } = event.detail;
            this.showTemporaryNotification(
                `✅ Dokument "${reviewRequest.name}" erfolgreich gespeichert`,
                3000
            );
        }) as EventListener);

        // Listen for failed saves
        globalSaveQueue.addEventListener('save-failed', ((event: CustomEvent) => {
            const { reviewRequest, error } = event.detail;
            this.showTemporaryNotification(
                `❌ Fehler beim Speichern von "${reviewRequest.name}": ${error}`,
                5000
            );
        }) as EventListener);
    }

    private resolveEnvironment(): string | undefined {
        const hitlApp = document.querySelector("hitl-app") as any;
        const detected = hitlApp?.detectedEnvironment;
        if (typeof detected === "string" && detected.trim().length > 0) {
            return detected.trim();
        }
        const prefix = process.env.CLIENT_PREFIX;
        const envName = process.env.ENVIRONMENT;
        if (prefix && envName) {
            return `${prefix}-${envName}`;
        }
        return undefined;
    }

    private resolveProjectId(reviewRequest: ReviewRequest): string | undefined {
        const envProject = process.env.PROJECT_ID;
        if (typeof envProject === "string" && envProject.trim().length > 0) {
            return envProject.trim();
        }

        if (reviewRequest instanceof PubSubReviewRequest) {
            if (reviewRequest.projectId && reviewRequest.projectId.trim().length > 0) {
                return reviewRequest.projectId.trim();
            }
        }

        const data = (reviewRequest as any).data ?? {};
        const processedData = data.documentData?.processed_data ?? data.processed_data ?? {};

        const directProject = this.pickFirstString(data, [
            "project_id",
            "projectId",
            "metadata.project_id",
            "metadata.projectId",
            "documentData.project_id",
            "documentData.projectId",
            "documentData.metadata.project_id",
            "documentData.metadata.projectId",
            "documentData.processed_data.project_id",
            "documentData.processed_data.projectId",
        ]);
        if (directProject) {
            return directProject;
        }

        const processorProject = this.pickFirstString(processedData, [
            "processorProject",
            "processor_project",
        ]) ?? this.pickFirstString(data, ["processorProject", "processor_project"]);
        if (processorProject) {
            const match = processorProject.match(/projects\/([^/]+)/);
            if (match) {
                return match[1];
            }
        }

        return undefined;
    }

    private buildValidationContext(
        reviewRequest: ReviewRequest,
        reviewedDocumentJson: any
    ): ValidationContext {
        const data = (reviewRequest as any).data ?? {};
        const processedData = data.documentData?.processed_data ?? data.processed_data ?? {};
        const metadata = data.documentData?.metadata ?? data.metadata ?? {};
        const docMetadata = reviewedDocumentJson?.metadata ?? {};

        const context: ValidationContext = {};

        context.business_area =
            this.pickFirstString(data, ["business_area"]) ??
            this.pickFirstString(processedData, ["business_area"]) ??
            this.pickFirstString(metadata, ["business_area"]) ??
            this.pickFirstString(docMetadata, ["business_area"]);

        context.use_case =
            this.pickFirstString(data, ["use_case", "use_case_id"]) ??
            this.pickFirstString(processedData, ["use_case", "use_case_id"]) ??
            this.pickFirstString(metadata, ["use_case", "use_case_id"]) ??
            this.pickFirstString(docMetadata, ["use_case", "use_case_id"]);

        context.document_class =
            this.pickFirstString(data, ["document_class"]) ??
            this.pickFirstString(processedData, ["document_class"]) ??
            this.pickFirstString(metadata, ["document_class"]) ??
            this.pickFirstString(docMetadata, ["document_class"]);

        context.processor_id =
            this.pickFirstString(data, ["processor_id"]) ??
            this.pickFirstString(processedData, ["processorId", "processor_id"]) ??
            (reviewRequest instanceof FirestoreReviewRequest
                ? this.pickFirstString(
                    reviewRequest.data?.documentData?.processed_data ?? {},
                    ["processorId", "processor_id"]
                )
                : undefined);

        context.processor_type =
            this.pickFirstString(processedData, ["processorType", "processor_type"]) ??
            this.pickFirstString(data, ["processor_type"]) ??
            (reviewRequest instanceof FirestoreReviewRequest
                ? reviewRequest.processorType
                : typeof reviewRequest.type === "string"
                    ? reviewRequest.type
                    : undefined);

        return Object.fromEntries(
            Object.entries(context).filter(
                ([, value]) => typeof value === "string" && value.trim().length > 0
            )
        );
    }

    private pickFirstString(source: any, paths: string[]): string | undefined {
        for (const path of paths) {
            const value = this.readNestedValue(source, path);
            if (typeof value === "string" && value.trim().length > 0) {
                return value.trim();
            }
        }
        return undefined;
    }

    private readNestedValue(source: any, path: string): unknown {
        if (!source || typeof source !== "object") {
            return undefined;
        }
        const segments = path.split(".");
        let current: any = source;
        for (const segment of segments) {
            if (!current || typeof current !== "object") {
                return undefined;
            }
            current = current[segment];
        }
        return current;
    }

    private showValidationErrorDialog(
        dialog: Dialog | null,
        errors: string[],
        version?: string
    ): void {
        if (!dialog) {
            console.error("Validierungsfehler:", errors);
            return;
        }
        const versionSuffix = version ? ` (Regelversion ${version})` : "";
        dialog.heading = "Dokumentvalidierung fehlgeschlagen";
        render(
            html`Die folgenden Validierungsfehler wurden festgestellt${versionSuffix}:<br/>
            <ul>
                ${errors.map((error) => html`
                    <li>${error}</li>`)}
            </ul>
            <mwc-button slot="primaryAction" dialogAction="ok">OK</mwc-button>`,
            dialog
        );
        dialog.open = true;
    }

    override render() {
        return html`
            <div id="documentAgainInHINTBanner"
                 style="height: 2vh; line-height: 2.2vh; color: rgb(255, 255, 255); text-align: center; background-color: rgb(176, 0, 32); display: none;">
                Dieses Dokument war bereits <span id="hintTrackerCount"></span> Mal im HINT. Bitte prüfen Sie die
                Pflichtfelder genau, damit das Dokument korrekt verarbeitet werden kann.
            </div>

            <!-- Save Status Indicator -->
            ${this.renderSaveStatusIndicator()}

            <!-- Keyboard Shortcuts Dialog -->
            <keyboard-shortcuts-dialog
                @click=${this.closeKeyboardShortcutsOnBackdrop}>
            </keyboard-shortcuts-dialog>

            <div class="content-div"></div>
        `;
    }

    /**
     * Open the custom keyboard shortcuts dialog
     */
    private async openKeyboardShortcuts() {
        console.log('Opening keyboard shortcuts dialog...');

        // Wait for next update cycle to ensure dialog is rendered
        await this.updateComplete;

        // Try multiple ways to find the dialog
        let dialog = this.shadowRoot?.querySelector('keyboard-shortcuts-dialog') as KeyboardShortcutsDialog;

        if (!dialog) {
            dialog = this.renderRoot?.querySelector('keyboard-shortcuts-dialog') as KeyboardShortcutsDialog;
        }

        if (!dialog) {
            // Try querying from document
            dialog = document.querySelector('keyboard-shortcuts-dialog') as KeyboardShortcutsDialog;
        }

        console.log('Dialog element:', dialog);
        console.log('ShadowRoot:', this.shadowRoot);
        console.log('RenderRoot:', this.renderRoot);

        if (dialog) {
            console.log('Calling show() on dialog');
            dialog.show();
        } else {
            console.error('Keyboard shortcuts dialog not found!');
            // Debug: list all elements in shadow DOM
            const allElements = this.shadowRoot?.querySelectorAll('*');
            console.log('All elements in shadow DOM:', allElements);
        }
    }

    /**
     * Close the keyboard shortcuts dialog when clicking on backdrop
     */
    private closeKeyboardShortcutsOnBackdrop(e: Event) {
        if (e.target === e.currentTarget) {
            const dialog = this.shadowRoot?.querySelector('keyboard-shortcuts-dialog') as KeyboardShortcutsDialog;
            if (dialog) {
                dialog.close();
            }
        }
    }

    private renderSaveStatusIndicator() {
        // Only show if there are pending or in-progress saves
        if (this.saveQueueStatus.pending === 0 && this.saveQueueStatus.inProgress === 0 && this.saveQueueStatus.failed === 0) {
            return html``;
        }

        const totalActive = this.saveQueueStatus.pending + this.saveQueueStatus.inProgress;

        return html`
            <style>
                .save-status-indicator {
                    position: fixed;
                    top: 80px;
                    right: 20px;
                    background: #4CAF50;
                    color: white;
                    padding: 12px 20px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    font-family: 'Roboto', sans-serif;
                    font-size: 14px;
                    z-index: 9999;
                    min-width: 200px;
                    animation: slideIn 0.3s ease;
                }

                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }

                .save-status-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 8px;
                    font-weight: 600;
                }

                .save-status-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }

                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                .save-status-details {
                    font-size: 12px;
                    opacity: 0.9;
                    margin-top: 4px;
                }

                .save-status-failed {
                    background: #f44336;
                    cursor: pointer;
                }

                .save-status-failed:hover {
                    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
                }
            </style>

            <div class="save-status-indicator ${this.saveQueueStatus.failed > 0 ? 'save-status-failed' : ''}"
                 @click=${this.handleRetryFailedSaves}>
                <div class="save-status-header">
                    ${this.saveQueueStatus.inProgress > 0 ? html`
                        <div class="save-status-spinner"></div>` : ''}
                    <span>
            ${this.saveQueueStatus.failed > 0
                ? '❌ Speicherfehler'
                : '💾 Speichert...'}
          </span>
                </div>
                <div class="save-status-details">
                    ${this.saveQueueStatus.inProgress > 0 ? html`⏳ ${this.saveQueueStatus.inProgress} aktiv<br>` : ''}
                    ${this.saveQueueStatus.pending > 0 ? html`📋 ${this.saveQueueStatus.pending} wartend<br>` : ''}
                    ${this.saveQueueStatus.failed > 0 ? html`
                        <span style="color: #ffeb3b;">⚠️ ${this.saveQueueStatus.failed} fehlgeschlagen</span><br>
                        <span style="font-size: 11px; text-decoration: underline;">Klicken zum erneuten Versuch</span>
                    ` : ''}
                </div>
            </div>
        `;
    }

    private handleRetryFailedSaves(): void {
        // Only retry if there are failed saves
        if (this.saveQueueStatus.failed === 0) {
            return;
        }

        // Get all failed operations
        const allOperations = globalSaveQueue.getAllOperations();
        const failedOperations = allOperations.filter(op => op.status === 'failed');

        if (failedOperations.length === 0) {
            return;
        }

        // Log retry action
        logAction({
            timestamp: new Date().toISOString(),
            severity: 'INFO',
            userId: this.userId,
            action: 'Manual retry of failed saves initiated',
            additionalInfo: {
                failedCount: failedOperations.length,
                operationIds: failedOperations.map(op => op.id),
            },
        });

        // Retry all failed operations
        failedOperations.forEach(operation => {
            globalSaveQueue.retryOperation(operation.id);
        });

        // Show feedback
        this.showTemporaryNotification(
            `${failedOperations.length} fehlgeschlagene Speichervorgänge werden erneut versucht...`,
            3000
        );
    }

    private batchInjectAccessTokens(urls: string[], accessToken: string, gcsOnly: boolean = false): string[] {
        // Optimized batch processing of URL token injection
        // Pre-compile the access token parameter to avoid repeated string concatenation
        const tokenParam = `access_token=${encodeURIComponent(accessToken)}`;

        return urls.map(url => {
            if (!url) return url;

            // For GCS-only mode, filter non-GCS URLs
            if (gcsOnly && !url.includes('storage.googleapis.com')) {
                return url;
            }

            // Optimized URL modification - avoid URL constructor when possible
            if (url.includes('?')) {
                return `${url}&${tokenParam}`;
            } else {
                return `${url}?${tokenParam}`;
            }
        });
    }

    private showProgressiveLoadingState(message: string) {
        const contentDiv = this.renderRoot.querySelector(".content-div") as HTMLElement;
        if (contentDiv) {
            contentDiv.innerHTML = `
        <div class="progressive-loading-container" style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 400px;
          color: #666;
          font-family: 'Roboto', sans-serif;
        ">
          <div class="loading-spinner" style="
            width: 40px;
            height: 40px;
            border: 4px solid #e0e0e0;
            border-top: 4px solid #1976d2;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          "></div>
          <div class="loading-message" style="
            font-size: 16px;
            color: #424242;
            text-align: center;
          ">${message}</div>
        </div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      `;
        }
    }

    private clearProgressiveLoadingState() {
        const contentDiv = this.renderRoot.querySelector(".content-div") as HTMLElement;
        if (contentDiv) {
            // Remove loading content - the actual document will be rendered by sdk.render
            const loadingContainer = contentDiv.querySelector(".progressive-loading-container");
            if (loadingContainer) {
                loadingContainer.remove();
            }
        }
    }

    private async waitForAIUIToLoad(): Promise<void> {
        return new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 100; // 10 seconds with 100ms intervals

            const checkForLoadedContent = () => {
                attempts++;
                const contentDiv = this.renderRoot.querySelector(".content-div") as HTMLElement;

                if (!contentDiv) {
                    if (attempts < maxAttempts) {
                        setTimeout(checkForLoadedContent, 100);
                        return;
                    } else {
                        console.warn('⚠️ Content div never found, proceeding anyway');
                        resolve();
                        return;
                    }
                }

                // Strategy 1: Check if the content div has any child elements at all
                const hasAnyContent = contentDiv.children.length > 0;

                // Strategy 2: Check for specific Google AI DocumentView patterns
                const aiPatterns = [
                    // Document AI specific patterns
                    'div[role="main"]',                    // Main content role
                    'div[role="document"]',                // Document role
                    'div[aria-label*="page"]',             // Page aria labels
                    'div[data-page]',                      // Page data attributes
                    '*[class*="documentai"]',              // DocumentAI classes
                    '*[class*="document-ai"]',             // Document-AI classes
                    // Image patterns
                    'img[src*="storage.googleapis.com"]', // GCS images
                    'img[src*="googleusercontent.com"]',  // Google user content
                    'img[alt*="page"]',                    // Page alt text
                    // Canvas and interactive elements
                    'canvas',                              // Canvas elements
                    'svg',                                 // SVG elements
                    // Generic content indicators
                    'div[style*="width"]',                 // Styled divs with width
                    'div[style*="height"]'                 // Styled divs with height
                ];

                let foundValidContent = false;

                if (hasAnyContent) {
                    // Check for AI patterns
                    for (const pattern of aiPatterns) {
                        const elements = contentDiv.querySelectorAll(pattern);
                        if (elements.length > 0) {
                            // For images, verify they're actually loaded
                            if (pattern.includes('img')) {
                                const images = Array.from(elements) as HTMLImageElement[];
                                const loadedImages = images.filter(img => {
                                    return img.complete && img.naturalHeight > 0 && img.naturalWidth > 0;
                                });
                                if (loadedImages.length > 0) {
                                    console.log(`🎯 Found ${loadedImages.length} loaded images via pattern: ${pattern}`);
                                    foundValidContent = true;
                                    break;
                                }
                            } else {
                                // For non-images, check if they have content or meaningful dimensions
                                const hasContent = Array.from(elements).some(el => {
                                    const htmlEl = el as HTMLElement;
                                    return htmlEl.offsetHeight > 10 && htmlEl.offsetWidth > 10;
                                });
                                if (hasContent) {
                                    console.log(`🎯 Found meaningful content via pattern: ${pattern}`);
                                    foundValidContent = true;
                                    break;
                                }
                            }
                        }
                    }

                    // Fallback: If content div has substantial content but no specific patterns match
                    if (!foundValidContent && contentDiv.innerHTML.length > 500) {
                        console.log('🎯 Content div has substantial content, considering it loaded');
                        foundValidContent = true;
                    }
                }

                if (foundValidContent) {
                    console.log('🎯 AI UI content detected and verified, waiting 200ms for final render');
                    setTimeout(resolve, 200);
                } else if (attempts >= maxAttempts) {
                    console.warn('⚠️ AI UI load detection timed out after 10 seconds, proceeding anyway');
                    console.log('Debug info:', {
                        contentDivExists: !!contentDiv,
                        childrenCount: contentDiv?.children.length || 0,
                        innerHTML: contentDiv?.innerHTML.substring(0, 200) || 'N/A'
                    });
                    resolve();
                } else {
                    // Continue checking every 100ms
                    setTimeout(checkForLoadedContent, 100);
                }
            };

            // Start checking immediately
            checkForLoadedContent();
        });
    }

    /**
     * Inject keyboard shortcuts button into AIUI toolbar
     */
    private injectKeyboardShortcutsButton(): void {
        // Wait for the AIUI toolbar to be available
        const checkForToolbar = () => {
            const contentDiv = this.renderRoot.querySelector(".content-div") as HTMLElement;
            if (!contentDiv) {
                return;
            }

            // Try to find the toolbar container in the shadow DOM
            const toolbar = contentDiv.querySelector('.main-panel-top-actions-left');

            if (toolbar && !toolbar.querySelector('.keyboard-shortcuts-toolbar-btn')) {
                console.log('🎹 Injecting keyboard shortcuts button into AIUI toolbar');

                // Create the button element
                const button = document.createElement('button');
                button.className = 'keyboard-shortcuts-toolbar-btn';
                button.title = 'Tastaturkürzel anzeigen / Show keyboard shortcuts';
                button.setAttribute('aria-label', 'Keyboard shortcuts');

                // Create the icon
                const icon = document.createElement('mwc-icon');
                icon.textContent = 'keyboard';
                button.appendChild(icon);

                // Add click handler
                button.addEventListener('click', () => {
                    this.openKeyboardShortcuts();
                });

                // Inject the button into the toolbar
                toolbar.appendChild(button);

                console.log('✅ Keyboard shortcuts button injected successfully');
            }
        };

        // Try immediately and also retry a few times in case the toolbar loads later
        checkForToolbar();
        setTimeout(checkForToolbar, 100);
        setTimeout(checkForToolbar, 300);
        setTimeout(checkForToolbar, 500);
    }

    /**
     * Inject Irrläufer button into AIUI toolbar
     */
    private injectIrrlaueferButton(): void {
        // Wait for the AIUI toolbar to be available
        const checkForToolbar = () => {
            const contentDiv = this.renderRoot.querySelector(".content-div") as HTMLElement;
            if (!contentDiv) {
                return;
            }

            // Try to find the toolbar container in the shadow DOM
            const toolbar = contentDiv.querySelector('.main-panel-top-actions-left');

            if (toolbar && !toolbar.querySelector('.irrlaeufer-toolbar-btn')) {
                console.log('🔀 Injecting Irrläufer button into AIUI toolbar');

                // Create the button element
                const button = document.createElement('button');
                button.className = 'irrlaeufer-toolbar-btn';
                button.title = 'Dokument als Irrläufer markieren / Mark document as wrong business area';
                button.setAttribute('aria-label', 'Irrläufer');

                // Create the icon
                const icon = document.createElement('mwc-icon');
                icon.textContent = 'compare_arrows';
                button.appendChild(icon);

                // Add click handler
                button.addEventListener('click', () => {
                    this.sendToIrrlaeufer();
                });

                // Inject the button into the toolbar
                toolbar.appendChild(button);

                console.log('✅ Irrläufer button injected successfully');
            }
        };

        // Try immediately and also retry a few times in case the toolbar loads later
        checkForToolbar();
        setTimeout(checkForToolbar, 100);
        setTimeout(checkForToolbar, 300);
        setTimeout(checkForToolbar, 500);
    }

    /**
     * Send document to Irrläufer with business area selection
     */
    private async sendToIrrlaeufer() {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        // Only works for Firestore review requests
        if (!(currentRequest instanceof FirestoreReviewRequest)) {
            console.warn('Irrläufer feature only available for Firestore requests');
            return;
        }

        logAction({
            timestamp: new Date().toISOString(),
            severity: 'INFO',
            userId: this.userId,
            action: 'Send to Irrläufer - Dialog Opened',
            additionalInfo: {
                reviewRequestName: currentRequest.name,
                envelopeId: currentRequest.envelopeId
            }
        });

        // Queue-to-business-area mapping loaded from infrastructure/locals.tf via environment variable
        const queueToBusinessAreaMap: Record<string, string> = JSON.parse(
            process.env.QUEUE_BUSINESS_AREA_MAP as string
        );

        // Determine current business area from queue name
        const queueName = currentRequest.queue || '';
        let currentBusinessArea = 'Sonstige Bereiche'; // Default
        for (const [key, area] of Object.entries(queueToBusinessAreaMap)) {
            if (queueName.toLowerCase().includes(key)) {
                currentBusinessArea = area;
                break;
            }
        }

        // Business areas loaded from infrastructure/locals.tf target_business_areas via environment variable
        const allBusinessAreas: string[] = JSON.parse(process.env.BUSINESS_AREAS as string);

        // Filter out current business area from selectable options
        const selectableBusinessAreas = allBusinessAreas.filter(area => area !== currentBusinessArea);

        // Submit handler for Irrläufer form (shared by button click and Enter key)
        const handleSubmit = async () => {
            // Get values from DOM elements
            const selectField = document.getElementById('irrlaeufer-business-area') as HTMLSelectElement;
            const textField = document.getElementById('irrlaeufer-comment') as any;

            const businessArea = selectField?.value || '';
            const comment = textField?.value || '';

            const trimmedBusinessArea = businessArea.trim();
            const trimmedComment = comment.trim();

            if (!trimmedBusinessArea || !trimmedComment) {
                alert('Bitte wählen Sie einen Geschäftsbereich und geben Sie einen Kommentar ein.');
                return;
            }

            // Close dialog after validation passes
            dialog.open = false;

            // Complete the task immediately with irrlaeufer flag
            try {
                const [accessToken, idToken] = await this.getAccessToken();

                // Get the original Document AI JSON content
                const content = await currentRequest.getContent(accessToken);
                const contentString = JSON.stringify(content);

                // Build data object with irrlaeufer flag
                const data = {
                    documentId: currentRequest.documentId,
                    envelopeId: currentRequest.envelopeId,
                    processorType: currentRequest.processorType,
                    userId: this.userId,
                    flags: {
                        irrlaeufer: {
                            business_area: trimmedBusinessArea,
                            comment: trimmedComment
                        }
                    }
                };

                logAction({
                    timestamp: new Date().toISOString(),
                    severity: 'INFO',
                    userId: this.userId,
                    action: 'Queueing Irrläufer save operation',
                    additionalInfo: {
                        reviewRequestName: currentRequest.name,
                        envelopeId: currentRequest.envelopeId,
                        businessArea: trimmedBusinessArea,
                        comment: trimmedComment
                    }
                });

                // Queue the save operation (no reject reason for Irrläufer)
                await globalSaveQueue.enqueueSave(
                    currentRequest,
                    accessToken,
                    idToken,
                    contentString,
                    data,
                    '', // No reject reason for Irrläufer
                    this.userId,
                    this.getAccessToken.bind(this)
                );

                // Show success message
                this.showTemporaryNotification(
                    `✓ Dokument als Irrläufer markiert (${trimmedBusinessArea})`,
                    3000
                );

                // Move to next document immediately (save happens in background)
                this.proceedToNextDocument();

            } catch (error) {
                console.error('Error marking document as Irrläufer:', error);
                alert('Fehler beim Speichern der Irrläufer-Markierung. Bitte versuchen Sie es erneut.');

                logAction({
                    timestamp: new Date().toISOString(),
                    severity: 'ERROR',
                    userId: this.userId,
                    action: 'Failed to mark document as Irrläufer',
                    additionalInfo: {
                        reviewRequestName: currentRequest.name,
                        envelopeId: currentRequest.envelopeId,
                        error: String(error)
                    }
                });
            }
        };

        // Keyboard handler for Enter key submission
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
            }
        };

        // Show dialog to get business area and comment from user
        const dialog = document.getElementById("dialog") as Dialog;
        if (dialog) {
            dialog.heading = "Irrläufer";
            render(
                html`
                    <p>Dieses Dokument gehört aktuell zu: <strong>${currentBusinessArea}</strong></p>
                    <p>Bitte wählen Sie den korrekten Geschäftsbereich und geben Sie einen Kommentar ein:</p>
                    <div style="margin: 16px 0;">
                        <label for="irrlaeufer-business-area" style="display: block; font-size: 12px; color: rgba(0, 0, 0, 0.6); margin-bottom: 4px;">Korrekter Geschäftsbereich *</label>
                        <select
                            id="irrlaeufer-business-area"
                            required
                            style="width: 100%; padding: 12px; font-size: 16px; border: 1px solid rgba(0, 0, 0, 0.38); border-radius: 4px; font-family: Roboto, sans-serif; background-color: white;"
                            @keydown=${handleKeyDown}
                        >
                            <option value="" disabled selected>Bitte wählen...</option>
                            ${selectableBusinessAreas.map(area => html`
                                <option value="${area}">${area}</option>
                            `)}
                        </select>
                    </div>
                    <mwc-textfield
                        id="irrlaeufer-comment"
                        label="Kommentar"
                        required
                        style="width: 100%; margin: 16px 0;"
                        maxlength="500"
                        helper="Max. 500 Zeichen"
                        @keydown=${handleKeyDown}
                    ></mwc-textfield>
                    <mwc-button
                        slot="secondaryAction"
                        dialogAction="cancel"
                    >Abbrechen</mwc-button>
                    <mwc-button
                        slot="primaryAction"
                        @click=${async (e: Event) => {
                            e.preventDefault();
                            await handleSubmit();
                        }}
                    >Senden</mwc-button>
                `,
                dialog
            );
            dialog.open = true;
        }
    }

    private logPerformanceMetrics(metrics: any, reviewRequest: ReviewRequest) {
        const roundedMetrics = {
            authTime: Math.round(metrics.authTime),
            contentLoadTime: Math.round(metrics.contentLoadTime),
            imageProcessingTime: Math.round(metrics.imageProcessingTime),
            renderTime: Math.round(metrics.renderTime),
            totalTime: Math.round(metrics.totalTime)
        };

        console.log('🚀 Document Review Performance Metrics:', {
            documentName: reviewRequest.name,
            processorType: reviewRequest instanceof FirestoreReviewRequest ? reviewRequest.processorType : reviewRequest.type,
            pageCount: reviewRequest.pageImageUrls.length,
            metrics: roundedMetrics,
            breakdown: {
                authPercentage: Math.round((metrics.authTime / metrics.totalTime) * 100),
                contentPercentage: Math.round((metrics.contentLoadTime / metrics.totalTime) * 100),
                imagePercentage: Math.round((metrics.imageProcessingTime / metrics.totalTime) * 100),
                renderPercentage: Math.round((metrics.renderTime / metrics.totalTime) * 100)
            }
        });

        // Log performance action for analytics
        logAction({
            timestamp: new Date().toISOString(),
            severity: "INFO",
            userId: this.userId,
            action: "Document Review Performance",
            additionalInfo: {
                reviewRequestName: reviewRequest.name,
                processorType: reviewRequest instanceof FirestoreReviewRequest ? reviewRequest.processorType : reviewRequest.type,
                pageCount: reviewRequest.pageImageUrls.length,
                ...roundedMetrics
            },
        });
    }

    async getAccessToken(refresh = false) {
        const accessToken = await new Promise<string>((resolve, reject) => {
            const getAccessToken = new CustomEvent("get-access-token", {
                bubbles: true,
                detail: { refresh: refresh, resolve: resolve, reject: reject },
            });
            this.dispatchEvent(getAccessToken);
        });
        return accessToken;
    }

    async reviewDocument(reviewRequest: ReviewRequest) {
        if (!reviewRequest) {
            console.error('reviewDocument called with undefined reviewRequest');
            this.showProgressiveLoadingState("Keine Aufgaben verfügbar. Bitte versuchen Sie es später erneut.");
            setTimeout(() => this.clearProgressiveLoadingState(), 3000);
            return;
        }

        try {
            // Start performance monitoring
            const performanceMetrics = {
                startTime: performance.now(),
                authTime: 0,
                contentLoadTime: 0,
                imageProcessingTime: 0,
                renderTime: 0,
                totalTime: 0
            };

        this.rejectReason = "";

        // Show immediate loading feedback
        this.showProgressiveLoadingState("Initializing components...");

        // Lazy load Material Components if not already loaded
        await loadMaterialComponents();

        // Update loading state
        this.showProgressiveLoadingState("Authenticating...");

        const authStart = performance.now();
        const [accessToken, idToken] = await this.getAccessToken();
        performanceMetrics.authTime = performance.now() - authStart;

        // Log the start of a document review with enriched info
        logAction({
            timestamp: new Date().toISOString(),
            severity: "INFO",
            userId: this.userId,
            action: "Started document review",
            additionalInfo: {
                reviewRequestName: reviewRequest.name,
                batch_id: reviewRequest.data?.batch_id,
                envelope_id: reviewRequest.data?.envelope_id,
                processorType: reviewRequest.data?.processorType,
                queue: reviewRequest.queue,
            },
        });

        // Update loading state
        this.showProgressiveLoadingState("Loading document content...");

        const contentStart = performance.now();
        const json = await reviewRequest.getContent(accessToken);
        performanceMetrics.contentLoadTime = performance.now() - contentStart;

        // Update loading state
        this.showProgressiveLoadingState("Processing images...");

        const imageStart = performance.now();

        // Optimized batch access token injection for page image URLs
        let pageImageUrls: string[];
        if (reviewRequest instanceof PubSubReviewRequest) {
            pageImageUrls = this.batchInjectAccessTokens(reviewRequest.pageImageUrls, accessToken);
        } else if (reviewRequest instanceof FirestoreReviewRequest) {
            // For Firestore requests, add access tokens to GCS URLs for authentication
            pageImageUrls = this.batchInjectAccessTokens(reviewRequest.pageImageUrls, accessToken, true);
        } else {
            pageImageUrls = reviewRequest.pageImageUrls;
        }

        performanceMetrics.imageProcessingTime = performance.now() - imageStart;

        console.log('DEBUG reviewDocument - reviewRequest type:', reviewRequest.constructor.name);
        console.log('DEBUG reviewDocument - json keys:', Object.keys(json || {}));
        console.log('DEBUG reviewDocument - json is Document AI format:', !!json.pages && !!json.entities);
        console.log('DEBUG reviewDocument - pageImageUrls count:', pageImageUrls.length);
        console.log('DEBUG reviewDocument - pageImageUrls:', pageImageUrls);
        console.log('DEBUG reviewDocument - processorType:', reviewRequest instanceof FirestoreReviewRequest ? reviewRequest.processorType : reviewRequest.type);
        // initialize human review state
        this.humanRevisionState = "requested";
        this.humanRevisionStateMessage = "";
        if (reviewRequest.labels.length === 0) {
            console.log("This is the reviewRequest", reviewRequest);

            // Handle Firestore review requests
            if (reviewRequest instanceof FirestoreReviewRequest) {
                console.log("DEBUG - Processing Firestore review request");
                console.log("DEBUG - Processor type:", reviewRequest.processorType);
                console.log("DEBUG - Full Firestore task data:", reviewRequest.data);
                console.log("DEBUG - Document AI JSON entities:", json.entities);

                // For Firestore classification, the labels come from the processed_data
                // which contains the complete handle_prediction_result output
                const taskLabels = reviewRequest.data?.documentData?.processed_data?.labels;

                console.log("DEBUG - Checking for taskLabels at processed_data.labels:", taskLabels);

                // For classification processors, use Firestore labels if available, otherwise fall back to Document AI entities
                if (reviewRequest.processorType === PROCESSOR_TYPES.CLASSIFICATION) {
                    if (taskLabels && Array.isArray(taskLabels) && taskLabels.length > 0) {
                        console.log("Classification processor: Using processed_data.labels");
                        console.log("Raw labels from processed_data:", taskLabels);

                        // Convert labels (which have displayName field) to EntityLabels for the classification dropdown
                        const classificationOptions = taskLabels.map((label: any, index: number) => {
                            console.log(`Processing label ${index}:`, label);
                            const displayName = label.displayName || label.label || label.name || `Option ${index + 1}`;
                            const id = label.id || index.toString();

                            // Augment display name with ID for hotkey support: <id> - <name>
                            // This allows users to press number keys to select classes directly
                            const augmentedDisplayName = `${id} - ${displayName}`;

                            const entityLabel = new EntityLabel(augmentedDisplayName);
                            entityLabel.id = id;

                            // Store original name for backend processing (without ID prefix)
                            (entityLabel as any).originalDisplayName = displayName;

                            console.log(`Created EntityLabel - Display: "${augmentedDisplayName}", Original: "${displayName}"`);
                            return entityLabel;
                        });

                        const singleClassificationLabel = new ClassificationLabel(classificationOptions);
                        singleClassificationLabel.label = "Document Classification";

                        // We don't have confidence scores in this format, so don't pre-select anything
                        // Let the user make the selection

                        reviewRequest.labels = [singleClassificationLabel];
                        console.log("Created classification label from processed_data:", reviewRequest.labels);

                    } else if (json.entities && json.entities.length > 0) {
                        console.log("Classification processor: No processed_data labels found, falling back to Document AI entities");
                        reviewRequest.labels = convertEntitiesToClassificationLabels(json.entities);
                        console.log("Generated classification labels from Document AI:", reviewRequest.labels);
                    } else {
                        console.log("Classification processor: No labels found anywhere");
                        reviewRequest.labels = [];
                    }
                }

                // For non-classification processors, use task labels if available
                else if (taskLabels && Array.isArray(taskLabels) && taskLabels.length > 0) {
                    console.log("Using task labels for non-classification processor:", taskLabels);

                    if (reviewRequest.processorType === PROCESSOR_TYPES.SPLITTING) {
                        reviewRequest.labels = taskLabels.map((label: any) => GroupingLabel.fromJson(label));
                    } else {
                        reviewRequest.labels = taskLabels.map((label: any) => EntityLabel.fromJson(label));
                    }

                    console.log("Successfully processed labels from task data:", reviewRequest.labels);
                } else if (json.entities) {
                    // Fallback: convert Document AI entities to labels (only if no task labels)
                    console.log("No task labels found, falling back to Document AI entity conversion");
                    console.log("DEBUG - Processor type for entity conversion:", reviewRequest.processorType);
                    console.log("DEBUG - Document AI entities to convert:", json.entities);
                    if (reviewRequest.processorType === PROCESSOR_TYPES.SPLITTING) {
                        reviewRequest.labels = convertEntitiesToGroupingLabels(json.entities);
                    } else if (reviewRequest.processorType === PROCESSOR_TYPES.CLASSIFICATION) {
                        console.log("Using convertEntitiesToClassificationLabels for processor type:", reviewRequest.processorType);
                        reviewRequest.labels = convertEntitiesToClassificationLabels(json.entities);
                        console.log("Generated classification labels:", reviewRequest.labels);
                    } else {
                        reviewRequest.labels = convertEntitiesToEntityLabels(json.entities);
                    }
                } else {
                    console.log("No labels found in task data, checking if we should fallback to Document AI entities");
                    console.log("DEBUG - Available data keys:", Object.keys(reviewRequest.data || {}));
                    console.log("DEBUG - Available json keys:", Object.keys(json || {}));

                    // For classification, if no processed_data labels, fallback to Document AI entities
                    if (reviewRequest.processorType === PROCESSOR_TYPES.CLASSIFICATION && json.entities && json.entities.length > 0) {
                        console.log("Classification processor: No processed_data labels found, falling back to Document AI entities");
                        reviewRequest.labels = convertEntitiesToClassificationLabels(json.entities);
                        console.log("Generated classification labels from Document AI entities as fallback:", reviewRequest.labels);
                    } else {
                        console.log("No suitable labels found anywhere, using empty array");
                        reviewRequest.labels = [];
                    }
                }
            } else if (json.entities) {
                // For PubSub and File requests, convert entities to labels
                // For PubSub and File requests, map legacy type field to standard processor types
                const processorType = reviewRequest.type === "DocSplitter" ? PROCESSOR_TYPES.SPLITTING :
                    reviewRequest.type === "DAI_COMPLEX_CLASSIFICATION" ? PROCESSOR_TYPES.CLASSIFICATION :
                        PROCESSOR_TYPES.EXTRACTION;

                if (processorType === PROCESSOR_TYPES.SPLITTING) {
                    reviewRequest.labels = convertEntitiesToGroupingLabels(json.entities);
                } else if (processorType === PROCESSOR_TYPES.CLASSIFICATION) {
                    reviewRequest.labels = convertEntitiesToClassificationLabels(json.entities);
                } else {
                    reviewRequest.labels = convertEntitiesToEntityLabels(json.entities);
                }
            } else {
                console.warn("No entities found in document, using empty labels");
                reviewRequest.labels = [];
            }
        }
        // add the revisions to the reviewRequest for documents that have been reviewed before
        if (json.hasOwnProperty("revisions")) {
            reviewRequest.revisions = json.revisions;
        }
        // Import the queue display name mapping function
        const { getQueueDisplayName } = await import('./mapping');

        // Get the display name for the queue
        const queueDisplayName = getQueueDisplayName(reviewRequest.queue);

        // Get envelope ID - for FirestoreReviewRequest it's a direct property, for others check data
        let envelopeId = 'N/A';
        if (reviewRequest instanceof FirestoreReviewRequest) {
            envelopeId = reviewRequest.envelopeId || 'N/A';
        } else if (reviewRequest.data?.envelope_id) {
            envelopeId = reviewRequest.data.envelope_id;
        } else if (reviewRequest.data?.envelopeId) {
            envelopeId = reviewRequest.data.envelopeId;
        }

        const headerTitle = `${this.reviewRequestsIndex + 1} / ${this.reviewRequests.length
            } ${queueDisplayName} | Sendung: ${envelopeId}`;
        // convert Document AI document Protbuf format to Document Input format
        // For Firestore requests, use the processorType from the task data
        // Map processor types to valid DocumentInput TabTypes using centralized mapping
        const mapProcessorTypeToTabType = (processorType: string): TabType => {
            return getTabTypeFromProcessorType(processorType);
        };

        const rawProcessorType = reviewRequest instanceof FirestoreReviewRequest
            ? reviewRequest.processorType // Use actual processor type from Firestore
            : reviewRequest.type; // Use request type for PubSub/File

        const documentType = mapProcessorTypeToTabType(rawProcessorType);

        // Set keyboard shortcut mode based on processor type
        hitlKeyboardShortcuts.setModeFromProcessorType(rawProcessorType);

        const documentInput = new DocumentInput(
            reviewRequest.labels,
            headerTitle,
            this.reviewRequestsIndex === 0,
            this.reviewRequests.length - 1 === this.reviewRequestsIndex,
            documentType,
            reviewRequest.queue,
        );

        // CRITICAL: For Firestore, follow EXACTLY the same pattern as PubSub
        // The AI UI component expects raw Document AI JSON format - DO NOT MODIFY IT
        // PubSub passes json unchanged from GCS, we must do the same

        console.log('DEBUG - Document AI JSON structure:');
        console.log('- Pages count:', json.pages?.length || 0);
        console.log('- Entities count:', json.entities?.length || 0);
        console.log('- Text length:', json.text?.length || 0);
        console.log('- Page image URLs:', pageImageUrls.length);

        // Create component client SDK instance with lazy loading
        // @ts-ignore
        const sdk = await this.getAIUISDK();
        console.log("sdk", sdk);
        // setup document view component and callback events
        var params = {
            component: "DocumentView",
            document: documentInput,
            // save event triggered immediately when primary button is clicked
            save: async () => {
                const reviewRequest = this.reviewRequests[this.reviewRequestsIndex];
                if (reviewRequest instanceof PubSubReviewRequest) {
                    // ensure that the access token gets refreshed if necessary
                    await this.getAccessToken(true);
                }
                if (reviewRequest instanceof FileReviewRequest) {
                    const pickerOptions = {
                        suggestedName: reviewRequest.suggestedName(),
                    };
                    // create a new handle
                    (reviewRequest as FileReviewRequest).outputFileSystemFileHandlePromise =
                        window.showSaveFilePicker(pickerOptions);
                }
            },
            jsonSave: async (reviewedDocumentJson: any) => {
                const reviewRequest = this.reviewRequests[this.reviewRequestsIndex];
                console.log("This is the reviewRequest", reviewRequest);
                const dialog = document.getElementById("dialog") as Dialog;

                const shouldSkipFieldValidation =
                    typeof this.rejectReason === "string" &&
                    this.rejectReason.trim().length > 0;

                let lastValidationVersion: string | undefined;

                if (!shouldSkipFieldValidation && reviewedDocumentJson.entities) {
                    const environment = this.resolveEnvironment();
                    const projectId = this.resolveProjectId(reviewRequest);

                    if (!environment || !projectId) {
                        this.showValidationErrorDialog(
                            dialog,
                            [
                                "Validierungsregeln konnten nicht geladen werden (fehlende Umgebung oder Projekt-ID).",
                            ]
                        );
                        return;
                    }

                    const validationContext = this.buildValidationContext(
                        reviewRequest,
                        reviewedDocumentJson
                    );

                    try {
                        const validationResult = await validateEntitiesWithRules({
                            entities: reviewedDocumentJson.entities,
                            context: validationContext,
                            accessToken,
                            idToken,
                            environment,
                            projectId,
                        });

                        // Check validation errors first, regardless of structure completeness
                        if (validationResult?.errors && validationResult.errors.length > 0) {
                            const formattedErrors = validationResult.errors.map((error) => {
                                const details = error.errors.join("; ");
                                const cleanedSuffix =
                                    error.cleanedValue !== error.originalValue
                                        ? ` (bereinigt: ${error.cleanedValue})`
                                        : "";
                                return `${error.fieldType}: ${details}${cleanedSuffix}`;
                            });
                            this.showValidationErrorDialog(
                                dialog,
                                formattedErrors,
                                validationResult.configVersion
                            );
                            return;
                        }

                        // If validation returned a complete result, use it
                        if (validationResult &&
                            validationResult.fieldResults &&
                            validationResult.errors &&
                            validationResult.warnings &&
                            validationResult.cleanedEntities) {
                            lastValidationVersion = validationResult.configVersion;

                            console.info(
                                "Validierung abgeschlossen",
                                {
                                    configVersion: validationResult.configVersion,
                                    totalResults: validationResult.fieldResults.length,
                                    errorsCount: validationResult.errors.length,
                                    warningsCount: validationResult.warnings.length,
                                    rulesLoaded: validationResult.fieldResults.filter(
                                        (r) => r.metadata?.source !== "no_rule"
                                    ).length,
                                    fieldsWithoutRules: validationResult.fieldResults
                                        .filter((r) => r.metadata?.source === "no_rule")
                                        .map((r) => r.fieldType),
                                }
                            );

                            if (validationResult.warnings.length > 0) {
                                console.info(
                                    "Feldvalidierung abgeschlossen mit Warnungen",
                                    validationResult.warnings.map((warning) => ({
                                        field: warning.fieldType,
                                        warnings: warning.warnings,
                                    }))
                                );
                            }

                            reviewedDocumentJson.entities = validationResult.cleanedEntities;
                        } else {
                            // Validation returned incomplete structure, log and proceed
                            console.warn(
                                "Validation returned incomplete result, proceeding without validation",
                                {
                                    hasValidationResult: !!validationResult,
                                    hasFieldResults: !!validationResult?.fieldResults,
                                    hasErrors: !!validationResult?.errors,
                                    hasWarnings: !!validationResult?.warnings,
                                    hasCleanedEntities: !!validationResult?.cleanedEntities,
                                    actualStructure: validationResult
                                }
                            );
                        }
                    } catch (validationError) {
                        console.error("Feldvalidierung nicht möglich", validationError);
                        // Don't block save on validation exception - continue to required field check
                        // This ensures required fields are still validated even if validation rules fail to load
                    }
                }

                // check if required labels have been provided with valid values
                const missingRequiredLabels = [];
                log.info("Checking required fields", {
                    totalLabels: reviewRequest.labels.length,
                    labels: reviewRequest.labels.map((l) => ({
                        label: l instanceof EntityLabel ? l.label : 'not EntityLabel',
                        requireCategory: l instanceof EntityLabel ? l.requireCategory : 'N/A'
                    }))
                });

                for (const label of reviewRequest.labels) {
                    if (label instanceof EntityLabel && label.requireCategory === "required") {
                        let found = false;
                        let isValid = false;
                        // Only check entities if they exist
                        if (reviewedDocumentJson.entities) {
                            for (const entity of reviewedDocumentJson.entities) {
                                if (entity.type === label.label) {
                                    found = true;
                                    // Check if field has a non-empty value after cleaning/validation
                                    const mentionText = entity.mention_text?.trim() || "";
                                    if (mentionText.length > 0) {
                                        isValid = true;
                                    }
                                }
                            }
                        }
                        // Required field is missing or empty after validation
                        if (!found || !isValid) {
                            missingRequiredLabels.push(label.label);
                            log.info(`Required field missing or invalid: ${label.label}`, {
                                found,
                                isValid
                            });
                        }
                    }
                }

                log.info("Required fields check complete", {
                    missingRequiredLabels,
                    totalMissing: missingRequiredLabels.length
                });

                // Check if classification has been selected for classification processors
                const processorType = reviewRequest instanceof FirestoreReviewRequest
                    ? reviewRequest.processorType
                    : reviewRequest.type;

                if ((processorType === PROCESSOR_TYPES.CLASSIFICATION || reviewRequest.type === "DAI_COMPLEX_CLASSIFICATION")
                    && this.rejectReason === "") {
                    // Check if there are classification labels
                    const classificationLabels = reviewRequest.labels.filter(label => label instanceof ClassificationLabel);

                    if (classificationLabels.length > 0) {
                        // Check if a classification value has been selected
                        const hasSelectedClassification = reviewedDocumentJson.entities?.some(entity =>
                            entity.type === "Document Classification" &&
                            entity.normalized_value?.text &&
                            entity.normalized_value.text.trim().length > 0
                        );

                        if (!hasSelectedClassification) {
                            dialog.heading = "Klassifizierung fehlt";
                            render(
                                html`Bitte wählen Sie eine Dokumentklassifikation aus, bevor Sie das Dokument speichern.
                                <mwc-button slot="primaryAction" dialogAction="ok">OK</mwc-button>`,
                                dialog
                            );
                            dialog.open = true;
                            return;
                        }
                    }
                }
                // check if entities marked for review have been reviewed
                const missingEntityReviews = [];
                // specific check if no rejectReason is provided (case if document is saved) and reviewDocumentJson contains an entities key
                if (this.rejectReason === "" && reviewedDocumentJson.entities != undefined) {
                    console.log("First basic check passed for saving: No storno reason and entities available");
                    for (const entity of reviewedDocumentJson.entities) {
                        console.log(entity);
                        if (entity.provenance?.type === 4) {
                            missingEntityReviews.push(entity.type);
                        }
                    }
                    // more specific check if regarding missing required entities and prevent saving
                    if (missingRequiredLabels.length > 0 || missingEntityReviews.length > 0) {
                        console.log("First more specific check not passed for saving: Missing entities");
                        dialog.heading = "Dokumentvalidierung fehlgeschlagen";
                        render(
                            html`Die folgenden Einträge weisen fehlende Werte auf: ${missingRequiredLabels.join(', ')}
                            <br>
                            Die folgenden erforderlichen Einträge wurden nicht überprüft: ${missingEntityReviews.join(', ')}
                            <mwc-button slot="primaryAction" dialogAction="ok"
                            >OK
                            </mwc-button
                            >`,
                            dialog
                        );
                        dialog.open = true;
                        return;
                    }
                }
                // specific check if no rejectReason is provided and reviewDocumentJson does not contain an entities key (to display error dialog which holds true for saving and storno in case of no data provided or no storno reason provided respectively)
                // prevent saving of empty document and throw dialog message
                else if (this.rejectReason === "" && reviewedDocumentJson.entities === undefined) {
                    console.log("Second check not passed for saving or storno: No entities in the reviewedDocumentJson and no rejectReason provided")
                    if (dialog) {
                        dialog.heading = "Dokumentvalidierung fehlgeschlagen";
                        render(
                            html`Sie haben keine Einträge vorgenommen.<br>
                            Überprüfen Sie bitte erneut das Dokument.
                            <mwc-button slot="primaryAction" dialogAction="ok"
                            >OK
                            </mwc-button
                            >`,
                            dialog
                        );
                        dialog.open = true;
                        return;
                    }
                } else if (this.rejectReason != "") {
                    console.log("Rejecting document with reason: ", this.rejectReason);
                }

                // Defensive: Strip ID prefix from classification entity types if not already stripped
                // This is a safety net - the main fix is in review-request.ts
                if ((processorType === PROCESSOR_TYPES.CLASSIFICATION || reviewRequest.type === "DAI_COMPLEX_CLASSIFICATION")
                    && reviewedDocumentJson.entities) {
                    for (const entity of reviewedDocumentJson.entities) {
                        if (entity.type && /^(\d+)\s*[-_]\s*/.test(entity.type)) {
                            entity.type = entity.type.replace(/^(\d+)\s*[-_]\s*/, '');
                        }
                    }
                }

                // Log before initiating document save
                logAction({
                    timestamp: new Date().toISOString(),
                    severity: "INFO",
                    userId: this.userId,
                    action: "Document save initiated",
                    additionalInfo: {
                        reviewRequestName: reviewRequest.name,
                        batch_id: reviewRequest.data?.batch_id,
                        envelope_id: reviewRequest.data?.envelope_id,
                        processorType: reviewRequest.data?.processorType,
                        queue: reviewRequest.queue,
                    },
                });

                // Create a new revision and prepare content for saving
                const revision = new Revision(
                    reviewedDocumentJson,
                    this.humanRevisionState,
                    this.humanRevisionStateMessage,
                    this.userId
                );

                // remove lastRevisionId which is not allowed in the output
                delete revision.lastRevisionId;
                reviewedDocumentJson.revisions.push(revision);
                const content = JSON.stringify(reviewedDocumentJson);

                // NON-BLOCKING SAVE: Enqueue the save operation instead of waiting for it
                // This allows the user to immediately proceed to the next document

                // Log before initiating save
                logAction({
                    timestamp: new Date().toISOString(),
                    severity: "INFO",
                    userId: this.userId,
                    action: "Document save queued (non-blocking)",
                    additionalInfo: {
                        reviewRequestName: reviewRequest.name,
                        batch_id: reviewRequest.data?.batch_id,
                        envelope_id: reviewRequest.data?.envelope_id,
                        processorType: reviewRequest.data?.processorType,
                        queue: reviewRequest.queue,
                    },
                });

                // Enqueue the save operation (returns immediately)
                // Note: Access token will be refreshed before each save attempt in the queue
                globalSaveQueue.enqueueSave(
                    reviewRequest,
                    accessToken,
                    idToken,
                    content,
                    reviewRequest.data,
                    this.rejectReason,
                    this.userId,
                    this.getAccessToken.bind(this) // Pass token refresh function
                );

                // Show immediate success feedback to user
                this.showTemporaryNotification(
                    'Dokument wird im Hintergrund gespeichert...',
                    2000
                );

                // Close dialog immediately
                if (dialog) {
                    dialog.open = false;
                }

                // Allow user to proceed to next document immediately
                this.proceedToNextDocument();
            },
            secondaryButtonClick: async () => {
                // ensure that the access token gets refreshed if necessary
                await this.getAccessToken(true);

                // Log the initial CLEARING button click
                const currentReviewRequest = this.reviewRequests[this.reviewRequestsIndex];

                // Debug logging to understand data structure
                console.log("DEBUG CLEARING - Request type:", currentReviewRequest.constructor.name);
                console.log("DEBUG CLEARING - Request data keys:", Object.keys(currentReviewRequest.data || {}));
                console.log("DEBUG CLEARING - Request data:", currentReviewRequest.data);
                console.log("DEBUG CLEARING - Current rejectReason:", this.rejectReason);

                const additionalInfo: any = {
                    reviewRequestName: currentReviewRequest.name,
                    batch_id: currentReviewRequest.data?.batch_id,
                    envelope_id: currentReviewRequest.data?.envelope_id,
                    processorType: currentReviewRequest.data?.processorType,
                    queue: currentReviewRequest.queue,
                    requestType: currentReviewRequest.constructor.name,
                    rejectReason: this.rejectReason || null, // Include current rejection reason (may be empty initially)
                };

                // Add additional processor information if available (for Firestore requests)
                if (currentReviewRequest instanceof FirestoreReviewRequest) {
                    additionalInfo.taskId = currentReviewRequest.taskId;
                    additionalInfo.documentId = currentReviewRequest.documentId;
                    additionalInfo.envelopeId = currentReviewRequest.envelopeId;
                    additionalInfo.priority = currentReviewRequest.priority;
                    additionalInfo.processorTypeFromRequest = currentReviewRequest.processorType;

                    // Debug for Firestore
                    console.log("DEBUG CLEARING - Firestore taskId:", currentReviewRequest.taskId);
                    console.log("DEBUG CLEARING - Firestore processorType:", currentReviewRequest.processorType);
                } else {
                    // For non-Firestore requests, try to get processor type from different locations
                    additionalInfo.legacyType = currentReviewRequest.type;
                    console.log("DEBUG CLEARING - Legacy type:", currentReviewRequest.type);
                }

                // Add key processor, envelope and document information
                if (currentReviewRequest.data) {
                    // Core envelope information
                    additionalInfo.envelope_id = currentReviewRequest.data.envelope_id;
                    additionalInfo.document_class = currentReviewRequest.data.document_class;
                    additionalInfo.original_filename = currentReviewRequest.data.original_filename;
                    additionalInfo.workflow_id = currentReviewRequest.data.workflow_id;
                    additionalInfo.submission_timestamp = currentReviewRequest.data.submission_timestamp;

                    // Processor information from various possible locations
                    additionalInfo.processor_type_alt = currentReviewRequest.data.processor_type;
                    additionalInfo.processor_id = currentReviewRequest.data.processor_id;
                    additionalInfo.task_id = currentReviewRequest.data.task_id;
                    additionalInfo.document_id = currentReviewRequest.data.document_id;
                    additionalInfo.priority = currentReviewRequest.data.priority;
                    additionalInfo.status = currentReviewRequest.data.status;

                    // Document data information
                    if (currentReviewRequest.data.documentData) {
                        additionalInfo.originalFileName = currentReviewRequest.data.documentData.originalFileName;
                        additionalInfo.documentId = currentReviewRequest.data.documentData.documentId;
                        additionalInfo.mimeType = currentReviewRequest.data.documentData.mimeType;

                        // Processor details from processed_data
                        if (currentReviewRequest.data.documentData.processed_data) {
                            const processedData = currentReviewRequest.data.documentData.processed_data;
                            additionalInfo.processorVersion = processedData.processorVersion;
                            additionalInfo.processorDisplayName = processedData.processorDisplayName;
                            additionalInfo.processorProject = processedData.processorProject;
                            additionalInfo.processorIdFromProcessedData = processedData.processorId;
                        }
                    }

                    // Add all available data keys for debugging
                    additionalInfo.availableDataKeys = Object.keys(currentReviewRequest.data);
                }

                // Add any processor-related data from the task data (legacy support)
                if (currentReviewRequest.data?.documentData?.processed_data) {
                    const processedData = currentReviewRequest.data.documentData.processed_data;
                    console.log("DEBUG CLEARING - Found processed_data:", processedData);
                }

                logAction({
                    timestamp: new Date().toISOString(),
                    severity: "INFO",
                    userId: this.userId,
                    action: "CLEARING button clicked",
                    additionalInfo,
                });

                // retrieve rejection reason from user
                const dialog = document.getElementById("dialog") as Dialog;
                if (dialog) {
                    // Clear any previous rejection reason before opening the dialog
                    this.rejectReason = "";
                    dialog.heading = "Tragen Sie hier den Storno-Grund ein.";
                    render(
                        html`
                            <mwc-textfield
                                    outlined
                                    style="width: 100%;margin-top: 12px;"
                                    required
                                    name="rejectReason"
                                    value=${this.rejectReason}
                                    label="Storno-Grund"
                                    @change=${this.handleFieldChange}
                                    @input=${this.handleFieldChange}
                                    @keydown=${(e: KeyboardEvent) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    this.rejectDocument(e);
                                }
                            }}
                            ></mwc-textfield>
                            <mwc-button
                                    id="dialogSubmitButton"
                                    label="CLEARING"
                                    slot="primaryAction"
                                    dialogAction="ok"
                                    @click=${this.rejectDocument}
                            ></mwc-button>`,
                        dialog
                    );
                    dialog.open = true;

                    // Auto-focus the textfield after dialog opens and ensure it's empty
                    requestAnimationFrame(() => {
                        const textfield = dialog.querySelector('mwc-textfield[name="rejectReason"]') as any;
                        if (textfield) {
                            textfield.value = "";  // Force clear the native input value
                            textfield.focus();
                        }
                    });
                }
            },
            navigate: async (event: NavEvent) => {
                // set a reviewEndTime for when a revision is interrupted by navigation across different document
                if (typeof reviewRequest.reviewDuration === 'undefined' || Number.isNaN(this.reviewDuration)) {
                    reviewRequest.reviewDuration = ((new Date()).getTime() - reviewRequest.reviewStartTime!.getTime()) / 1000;
                } else {
                    reviewRequest.reviewDuration += ((new Date()).getTime() - reviewRequest.reviewStartTime!.getTime()) / 1000;
                }
                console.log("Set the reviewDuration (seconds) due to navigation to", reviewRequest.reviewDuration);

                // ensure that the access token gets refreshed if necessary
                await this.getAccessToken(true);

                let actionLabel = "";
                if (event === NavEvent.BACK) {
                    actionLabel = "Navigate back";
                } else if (event === NavEvent.PREVIOUS) {
                    actionLabel = "Navigate previous";
                } else if (event === NavEvent.NEXT) {
                    actionLabel = "Navigate next";
                }
                logAction({
                    timestamp: new Date().toISOString(),
                    severity: "INFO",
                    userId: this.userId,
                    action: actionLabel,
                    additionalInfo: {
                        reviewRequestName: reviewRequest.name,
                        batch_id: reviewRequest.data?.batch_id,
                        envelope_id: reviewRequest.data?.envelope_id,
                        processorType: reviewRequest.data?.processorType,
                        queue: reviewRequest.queue,
                    },
                });

                if (event === NavEvent.BACK) {
                    // back button clicked
                    this.closeReview();
                    //Stop displaying the banner in case it was being displayed
                    document.getElementById("documentAgainInHINTBanner").style.display = "none";
                } else {
                    if (event === NavEvent.PREVIOUS) {
                        // previous button clicked
                        this.reviewRequestsIndex = Math.max(
                            this.reviewRequestsIndex - 1,
                            0
                        );
                    } else if (event === NavEvent.NEXT) {
                        // next button clicked
                        this.reviewRequestsIndex = Math.min(
                            this.reviewRequestsIndex + 1,
                            this.reviewRequests.length - 1
                        );
                    }
                    //Stop displaying the banner in case it was being displayed
                    document.getElementById("documentAgainInHINTBanner").style.display = "none";
                    this.reviewDocument(this.reviewRequests[this.reviewRequestsIndex]);
                }
            },
            escalate: async () => {
                // ensure that the access token gets refreshed if necessary
                await this.getAccessToken(true);
                const currentReviewRequest = this.reviewRequests[this.reviewRequestsIndex];
                // Log escalation request with enriched info
                logAction({
                    timestamp: new Date().toISOString(),
                    severity: "INFO",
                    userId: this.userId,
                    action: "Escalation requested",
                    additionalInfo: {
                        reviewRequestName: currentReviewRequest.name,
                        batch_id: currentReviewRequest.data?.batch_id,
                        envelope_id: currentReviewRequest.data?.envelope_id,
                        processorType: currentReviewRequest.data?.processorType,
                        queue: currentReviewRequest.queue,
                    },
                });
                const dialog = document.getElementById("dialog") as Dialog;
                if (dialog) {
                    dialog.heading = "Dokument wird eskaliert.";
                    render(
                        html`
                            <mwc-button slot="primaryAction" dialogAction="ok" @click=${this.escalateDocument}
                                        data-value=${accessToken}
                            >ESKALIEREN
                            </mwc-button
                            >`,
                        dialog
                    );
                    dialog.open = true;
                }
            },
        };
        // retrieve element for rendering the main document view UI component
        const aiDocumentView = this.renderRoot.querySelector(".content-div");

        // Add mode-specific class for CSS targeting
        if (aiDocumentView) {
            // Remove any existing mode classes
            aiDocumentView.classList.remove('docsplitter-mode', 'classification-mode', 'entity-mode');

            // Add appropriate mode class
            if (documentType === "DocSplitter") {
                aiDocumentView.classList.add('docsplitter-mode');
            } else if (documentType === "DAI_COMPLEX_CLASSIFICATION") {
                aiDocumentView.classList.add('classification-mode');
            } else if (documentType === "Entity") {
                aiDocumentView.classList.add('entity-mode');
            }

            // Force cleanup of any corrupted React root state from a previous failed render
            aiDocumentView.innerHTML = "";
        }

        // Final loading state before rendering
        this.showProgressiveLoadingState("Loading document viewer...");

        const renderStart = performance.now();
        this.documentViewElement = await sdk.render(aiDocumentView, params);
        performanceMetrics.renderTime = performance.now() - renderStart;

        if (!this.documentViewElement) {
            console.error("sdk.render returned undefined. aiDocumentView exists:", !!aiDocumentView);
            throw new Error("Fehler bei der Initialisierung der Dokumentenansicht. Bitte laden Sie das Dokument neu.");
        }

        // Wait for AI UI component to be fully loaded and visible before clearing loading state
        this.waitForAIUIToLoad().then(() => {
            this.clearProgressiveLoadingState();

            // Calculate total time and log performance metrics (after full UI load)
            performanceMetrics.totalTime = performance.now() - performanceMetrics.startTime;
            this.logPerformanceMetrics(performanceMetrics, reviewRequest);

            // Inject keyboard shortcuts button into AIUI toolbar
            this.injectKeyboardShortcutsButton();

            // Inject Irrläufer button into AIUI toolbar
            this.injectIrrlaueferButton();
        });
        if (documentType === "DocSplitter") {
            this.documentViewElement.isSplitter = true;
            this.documentViewElement.showSearchButton = false;
            // Add data attribute to enable CSS hiding
            const contentDiv = this.renderRoot.querySelector(".content-div");
            if (contentDiv) {
                contentDiv.setAttribute("data-type", "docsplitter");
            }
        } else {
            // For all other types (Entity, etc.), set data attribute to allow search
            const contentDiv = this.renderRoot.querySelector(".content-div");
            if (contentDiv) {
                contentDiv.setAttribute("data-type", "entity");
            }
        }
        if (reviewRequest.type === "Entity" && typeof reviewRequest.revisions !== 'undefined') {
            //extract the id of the last object that got pushed to the revisions array
            let hintTrackerCount = parseInt(reviewRequest.revisions.at(-1).id);
            //insert the amount of revisions into the banner
            document.getElementById("hintTrackerCount").innerHTML = hintTrackerCount;
            //display the banner
            console.log("Showing the Banner again");
            document.getElementById("documentAgainInHINTBanner").style.display = "block";
        }
        // Debug: log the final json structure and pageImageUrls
        console.log("Final json structure for AI UI:", json);
        console.log("Final pageImageUrls for AI UI:", pageImageUrls);
        console.log("Final documentInput.data:", documentInput.data);
        console.log('DEBUG AI UI - json.pages structure:', json.pages?.map((p, i) => ({
            pageIndex: i,
            hasImageUrl: !!p.imageUrl,
            imageUrlType: typeof p.imageUrl,
            hasTokens: !!p.tokens,
            hasEntities: !!p.entities,
            hasBlocks: !!p.blocks,
            pageKeys: Object.keys(p || {})
        })));
        console.log('DEBUG AI UI - documentInput.data.pages structure:', documentInput.data.pages?.map((p, i) => ({
            pageIndex: i,
            hasImageUrl: !!p.imageUrl,
            imageUrlType: typeof p.imageUrl,
            imageUrlValue: p.imageUrl?.privateDoNotAccessOrElseWrappedUrl ? 'has privateDoNotAccessOrElseWrappedUrl' : p.imageUrl,
            hasTokens: !!p.tokens,
            hasEntities: !!p.entities,
            pageKeys: Object.keys(p || {})
        })));
        console.log('DEBUG AI UI - entities available:', {
            jsonEntities: json.entities?.length || 0,
            documentInputEntities: documentInput.data.entities?.length || 0,
            entityLabels: documentInput.data.entityLabels?.length || 0
        });
            // Set the document data for AI UI - Follow PubSub approach exactly
            this.documentViewElement.docProtoJson = { json: json, imageUrls: pageImageUrls };


            reviewRequest.reviewStartTime = new Date();
            console.log("Set the reviewStartTime for the document to: ", reviewRequest.reviewStartTime);

            // Smart preloading: Start loading next document in background
            this.preloadNextDocument();

            // Batch preload images for better performance
            if (reviewRequest instanceof FirestoreReviewRequest && pageImageUrls.length > 1) {
                // Don't await - preload in background
                FirestoreReviewRequest.preloadImages(pageImageUrls, accessToken).catch(error => {
                    console.warn('Image preloading failed:', error);
                });
            }
        } catch (error) {
            console.error('Error loading document for review:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.lastDocumentLoadError = errorMessage;

            // Always clear the loading spinner to prevent it from being stuck
            this.clearProgressiveLoadingState();

            // Show error dialog using existing Material Dialog pattern
            const dialog = document.getElementById("dialog") as Dialog;
            if (dialog) {
                dialog.heading = "Fehler beim Laden des Dokuments";
                render(
                    html`
                        <p>Das Dokument konnte nicht geladen werden.</p>
                        <p style="color: #666; font-size: 14px;">
                            ${error instanceof Error ? error.message : 'Unbekannter Fehler'}
                        </p>
                        <p>Möchten Sie die Aufgabe aus der Bearbeitungsqueue entfernen und zum nächsten Dokument gehen?</p>
                        <mwc-button slot="primaryAction" @click=${(event: Event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void this.discardTaskAndProceed(dialog);
                        }}>Ja</mwc-button>
                        <mwc-button slot="secondaryAction" dialogAction="close" @click=${() => {
                            dialog.close();
                            this.closeReview();
                        }}>Nein, Zurück zur Startseite</mwc-button>
                    `,
                    dialog
                );
                dialog.open = true;
            }

            // Log the error for monitoring
            logAction({
                timestamp: new Date().toISOString(),
                severity: 'ERROR',
                userId: this.userId,
                action: 'Document loading failed',
                additionalInfo: {
                    reviewRequestName: reviewRequest.name,
                    error: errorMessage,
                    queue: reviewRequest.queue,
                    envelopeId: reviewRequest instanceof FirestoreReviewRequest
                        ? reviewRequest.envelopeId : undefined
                }
            });
        }
    }

    /**
     * Lazy load AI UI SDK for better bundle size and initial load performance
     */
    private async getAIUISDK(): Promise<any> {
        // @ts-ignore - AI UI SDK is loaded globally
        if (typeof AIUIComponentClient !== 'undefined') {
            // @ts-ignore
            return new AIUIComponentClient(process.env.API_KEY);
        }

        // If not available, this is a development fallback
        console.warn('AI UI SDK not available - using fallback');
        return {
            render: () => console.warn('AI UI SDK not loaded')
        };
    }

    /**
     * Smart preloading: Preload next document in background for better performance
     */
    private async preloadNextDocument(): Promise<void> {
        const nextIndex = this.reviewRequestsIndex + 1;
        if (nextIndex >= this.reviewRequests.length) {
            return; // No next document to preload
        }

        const nextRequest = this.reviewRequests[nextIndex];
        console.log(`🔄 Preloading next document (${nextIndex + 1}/${this.reviewRequests.length}): ${nextRequest.name}`);

        try {
            const [accessToken, idToken] = await this.getAccessToken();
            // Preload in background - don't await, just let it load
            nextRequest.getContent(accessToken).then(() => {
                console.log(`✅ Successfully preloaded document: ${nextRequest.name}`);
            }).catch(error => {
                console.warn(`⚠️ Failed to preload document: ${nextRequest.name}`, error);
            });
        } catch (error) {
            console.warn(`⚠️ Failed to start preloading for document: ${nextRequest.name}`, error);
        }
    }

    /**
     * Global field change handler.
     * If the changed field is "rejectReason", update the rejectReason property.
     * Logs every field change with enriched context.
     */
    handleFieldChange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!target || !target.name) return;
        const newValue = target.value;
        const fieldName = target.name;
        // If the changed field is the reject reason, update the property
        if (fieldName === "rejectReason") {
            this.rejectReason = newValue;
        }
        const currentReviewRequest = this.reviewRequests[this.reviewRequestsIndex];
        logAction({
            timestamp: new Date().toISOString(),
            severity: "INFO",
            userId: this.userId,
            action: "Field changed",
            additionalInfo: {
                fieldName: fieldName,
                newValue: newValue,
                batch_id: currentReviewRequest.data?.batch_id,
                envelope_id: currentReviewRequest.data?.envelope_id,
                processorType: currentReviewRequest.data?.processorType,
                queue: currentReviewRequest.queue,
            },
        });
    };

    // Legacy hotkey handler - now replaced by centralized keyboard shortcuts
    handleHotkeys = (e: KeyboardEvent) => {
        // Keep existing Ctrl+S functionality for backward compatibility
        if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
            e.preventDefault();
            this.clickSaveButton();
        }
    };

    private clickSaveButton() {
        let saveButton: HTMLButtonElement | null = null;
        if (this.documentViewElement) {
            if (this.documentViewElement.shadowRoot) {
                saveButton = this.documentViewElement.shadowRoot.querySelector<HTMLButtonElement>(".primary-button");
            }
            if (!saveButton) {
                saveButton = this.documentViewElement.querySelector<HTMLButtonElement>(".primary-button");
            }
        }
        if (saveButton) {
            console.log("Simulating click on save button via hotkey.");
            saveButton.click();
        } else {
            console.error("Save button not found; cannot trigger save action.");
        }
    }

    /**
     * Zoom in on the document view (keyboard layout-independent)
     * Uses event.code for physical key detection
     */
    private zoomIn() {
        let zoomInButton: HTMLButtonElement | null = null;
        if (this.documentViewElement) {
            // Try multiple selector patterns to find the zoom in button
            const selectors = [
                '[aria-label*="Zoom in"]',
                '[title*="Zoom in"]',
                '.zoom-in',
                '.zoom-in-button',
                'button[aria-label*="zoom in" i]'
            ];

            if (this.documentViewElement.shadowRoot) {
                for (const selector of selectors) {
                    zoomInButton = this.documentViewElement.shadowRoot.querySelector<HTMLButtonElement>(selector);
                    if (zoomInButton) break;
                }
            }

            if (!zoomInButton) {
                for (const selector of selectors) {
                    zoomInButton = this.documentViewElement.querySelector<HTMLButtonElement>(selector);
                    if (zoomInButton) break;
                }
            }
        }

        if (zoomInButton) {
            log.debug("Zooming in via keyboard shortcut (layout-independent)");
            zoomInButton.click();
        } else {
            log.warn("Zoom in button not found in AIUI component");
        }
    }

    /**
     * Zoom out on the document view (keyboard layout-independent)
     * Uses event.code for physical key detection
     */
    private zoomOut() {
        let zoomOutButton: HTMLButtonElement | null = null;
        if (this.documentViewElement) {
            // Try multiple selector patterns to find the zoom out button
            const selectors = [
                '[aria-label*="Zoom out"]',
                '[title*="Zoom out"]',
                '.zoom-out',
                '.zoom-out-button',
                'button[aria-label*="zoom out" i]'
            ];

            if (this.documentViewElement.shadowRoot) {
                for (const selector of selectors) {
                    zoomOutButton = this.documentViewElement.shadowRoot.querySelector<HTMLButtonElement>(selector);
                    if (zoomOutButton) break;
                }
            }

            if (!zoomOutButton) {
                for (const selector of selectors) {
                    zoomOutButton = this.documentViewElement.querySelector<HTMLButtonElement>(selector);
                    if (zoomOutButton) break;
                }
            }
        }

        if (zoomOutButton) {
            log.debug("Zooming out via keyboard shortcut (layout-independent)");
            zoomOutButton.click();
        } else {
            log.warn("Zoom out button not found in AIUI component");
        }
    }

    /**
     * Zoom to fit the document view (keyboard layout-independent)
     * Uses event.code for physical key detection
     */
    private zoomToFit() {
        let zoomToFitButton: HTMLButtonElement | null = null;
        if (this.documentViewElement) {
            // Try multiple selector patterns to find the zoom to fit button
            const selectors = [
                '[aria-label*="Zoom to fit"]',
                '[aria-label*="Fit to page"]',
                '[aria-label*="Fit"]',
                '[title*="Zoom to fit"]',
                '[title*="Fit to page"]',
                '.zoom-to-fit',
                '.fit-to-page',
                'button[aria-label*="fit" i]'
            ];

            if (this.documentViewElement.shadowRoot) {
                for (const selector of selectors) {
                    zoomToFitButton = this.documentViewElement.shadowRoot.querySelector<HTMLButtonElement>(selector);
                    if (zoomToFitButton) break;
                }
            }

            if (!zoomToFitButton) {
                for (const selector of selectors) {
                    zoomToFitButton = this.documentViewElement.querySelector<HTMLButtonElement>(selector);
                    if (zoomToFitButton) break;
                }
            }
        }

        if (zoomToFitButton) {
            log.debug("Zooming to fit via keyboard shortcut (layout-independent)");
            zoomToFitButton.click();
        } else {
            log.warn("Zoom to fit button not found in AIUI component");
        }
    }

    /**
     * Undo last annotation action (keyboard layout-independent)
     * Uses event.code for physical key detection
     */
    private undo() {
        let undoButton: HTMLButtonElement | null = null;
        if (this.documentViewElement) {
            // Try multiple selector patterns to find the undo button
            const selectors = [
                '[aria-label*="Undo"]',
                '[title*="Undo"]',
                '.undo-button',
                'button[aria-label*="undo" i]'
            ];

            if (this.documentViewElement.shadowRoot) {
                for (const selector of selectors) {
                    undoButton = this.documentViewElement.shadowRoot.querySelector<HTMLButtonElement>(selector);
                    if (undoButton) break;
                }
            }

            if (!undoButton) {
                for (const selector of selectors) {
                    undoButton = this.documentViewElement.querySelector<HTMLButtonElement>(selector);
                    if (undoButton) break;
                }
            }
        }

        if (undoButton) {
            log.debug("Undo via keyboard shortcut (layout-independent)");
            undoButton.click();
        } else {
            log.warn("Undo button not found in AIUI component");
        }
    }

    /**
     * Redo last undone annotation action (keyboard layout-independent)
     * Uses event.code for physical key detection
     */
    private redo() {
        let redoButton: HTMLButtonElement | null = null;
        if (this.documentViewElement) {
            // Try multiple selector patterns to find the redo button
            const selectors = [
                '[aria-label*="Redo"]',
                '[title*="Redo"]',
                '.redo-button',
                'button[aria-label*="redo" i]'
            ];

            if (this.documentViewElement.shadowRoot) {
                for (const selector of selectors) {
                    redoButton = this.documentViewElement.shadowRoot.querySelector<HTMLButtonElement>(selector);
                    if (redoButton) break;
                }
            }

            if (!redoButton) {
                for (const selector of selectors) {
                    redoButton = this.documentViewElement.querySelector<HTMLButtonElement>(selector);
                    if (redoButton) break;
                }
            }
        }

        if (redoButton) {
            log.debug("Redo via keyboard shortcut (layout-independent)");
            redoButton.click();
        } else {
            log.warn("Redo button not found in AIUI component");
        }
    }

    rejectDocument = async (e: Event) => {
        // ensure that the access token gets refreshed if necessary
        await this.getAccessToken(true);

        this.humanRevisionStateMessage = this.rejectReason;
        this.humanRevisionState = "rejected";
        const currentReviewRequest = this.reviewRequests[this.reviewRequestsIndex];
        const dialog = document.getElementById("dialog") as Dialog;
        if (this.rejectReason != "") {
            // Log comprehensive document rejection with enhanced information
            const additionalInfo: any = {
                reviewRequestName: currentReviewRequest.name,
                batch_id: currentReviewRequest.data?.batch_id,
                envelope_id: currentReviewRequest.data?.envelope_id,
                processorType: currentReviewRequest.data?.processorType,
                queue: currentReviewRequest.queue,
                requestType: currentReviewRequest.constructor.name,
                rejectReason: this.rejectReason,
            };

            // Add additional processor information if available (for Firestore requests)
            if (currentReviewRequest instanceof FirestoreReviewRequest) {
                additionalInfo.taskId = currentReviewRequest.taskId;
                additionalInfo.documentId = currentReviewRequest.documentId;
                additionalInfo.envelopeId = currentReviewRequest.envelopeId;
                additionalInfo.priority = currentReviewRequest.priority;
                additionalInfo.processorTypeFromRequest = currentReviewRequest.processorType;
            } else {
                additionalInfo.legacyType = currentReviewRequest.type;
            }

            // Add key processor, envelope and document information
            if (currentReviewRequest.data) {
                // Core envelope information
                additionalInfo.envelope_id = currentReviewRequest.data.envelope_id;
                additionalInfo.document_class = currentReviewRequest.data.document_class;
                additionalInfo.original_filename = currentReviewRequest.data.original_filename;
                additionalInfo.workflow_id = currentReviewRequest.data.workflow_id;
                additionalInfo.submission_timestamp = currentReviewRequest.data.submission_timestamp;

                // Processor information from various possible locations
                additionalInfo.processor_type_alt = currentReviewRequest.data.processor_type;
                additionalInfo.processor_id = currentReviewRequest.data.processor_id;
                additionalInfo.task_id = currentReviewRequest.data.task_id;
                additionalInfo.document_id = currentReviewRequest.data.document_id;
                additionalInfo.priority = currentReviewRequest.data.priority;
                additionalInfo.status = currentReviewRequest.data.status;

                // Document data information
                if (currentReviewRequest.data.documentData) {
                    additionalInfo.originalFileName = currentReviewRequest.data.documentData.originalFileName;
                    additionalInfo.documentId = currentReviewRequest.data.documentData.documentId;
                    additionalInfo.mimeType = currentReviewRequest.data.documentData.mimeType;

                    // Processor details from processed_data
                    if (currentReviewRequest.data.documentData.processed_data) {
                        const processedData = currentReviewRequest.data.documentData.processed_data;
                        additionalInfo.processorVersion = processedData.processorVersion;
                        additionalInfo.processorDisplayName = processedData.processorDisplayName;
                        additionalInfo.processorProject = processedData.processorProject;
                        additionalInfo.processorIdFromProcessedData = processedData.processorId;
                    }
                }

                // Add all available data keys for debugging
                additionalInfo.availableDataKeys = Object.keys(currentReviewRequest.data);
            }

            logAction({
                timestamp: new Date().toISOString(),
                severity: "INFO",
                userId: this.userId,
                action: "Document rejected",
                additionalInfo,
            });
            // Close the dialog before triggering save
            if (dialog) {
                dialog.open = false;
            }
            // Try to trigger the save action by looking for the primary save button.
            let saveButton: HTMLButtonElement | null = null;
            if (this.documentViewElement) {
                // Try the shadow DOM first...
                if (this.documentViewElement.shadowRoot) {
                    saveButton = this.documentViewElement.shadowRoot.querySelector(".primary-button") as HTMLButtonElement;
                }
                // If not found, try the light DOM of the DocumentView element
                if (!saveButton) {
                    saveButton = this.documentViewElement.querySelector(".primary-button") as HTMLButtonElement;
                }
            }
            if (saveButton) {
                console.log("Simulating click on save button.");
                saveButton.click();
            } else {
                console.error("Save button not found; cannot trigger save action.");
            }
        } else {
            console.log("Stopping propagation due to missing rejection reason");
            e.stopPropagation();
            e.preventDefault();
            if (dialog) {
                // Instead of re-rendering the entire dialog content, just update the textfield focus and validation
                const existingTextField = dialog.querySelector('mwc-textfield[name="rejectReason"]') as any;
                if (existingTextField) {
                    // Focus the existing textfield and show validation error
                    existingTextField.focus();
                    existingTextField.setCustomValidity("Bitte geben Sie einen Storno-Grund ein.");
                    existingTextField.reportValidity();

                    // Clear the validation error when user starts typing
                    existingTextField.addEventListener('input', () => {
                        existingTextField.setCustomValidity("");
                    }, { once: true });
                } else {
                    // Fallback: re-render if textfield not found, but ensure proper event handling
                    dialog.heading = "Tragen Sie hier den Storno-Grund ein.";
                    render(
                        html`
                            <mwc-textfield
                                    outlined
                                    style="width: 100%; margin-top: 12px;"
                                    required
                                    name="rejectReason"
                                    value=${this.rejectReason}
                                    label="Fehlenden Storno-Grund eingeben"
                                    @change=${this.handleFieldChange}
                                    @input=${this.handleFieldChange}
                                    @keydown=${(e: KeyboardEvent) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    this.rejectDocument(e);
                                }
                            }}
                            ></mwc-textfield>
                            <mwc-button
                                    label="CLEARING"
                                    slot="primaryAction"
                                    dialogAction="ok"
                                    @click=${this.rejectDocument}
                            ></mwc-button>`,
                        dialog
                    );
                }
                // Ensure dialog remains open
                dialog.open = true;

                // Auto-focus the textfield after dialog re-renders
                requestAnimationFrame(() => {
                    const textfield = dialog.querySelector('mwc-textfield[name="rejectReason"]') as any;
                    if (textfield) {
                        textfield.focus();
                    }
                });
            }
        }
    };

    closeReview() {
        const currentReviewRequest = this.reviewRequests[this.reviewRequestsIndex] || {};
        logAction({
            timestamp: new Date().toISOString(),
            severity: "INFO",
            userId: this.userId,
            action: "Review session closed",
            additionalInfo: {
                reviewRequestName: currentReviewRequest.name,
                batch_id: currentReviewRequest.data?.batch_id,
                envelope_id: currentReviewRequest.data?.envelope_id,
                processorType: currentReviewRequest.data?.processorType,
                queue: currentReviewRequest.queue,
            },
        });

        const reviewRequestUpdatedEvent = new CustomEvent(
            "manage-batch-and-queues-triggered",
            {
                bubbles: true,
                detail: this.reviewRequests,
            }
        );
        this.dispatchEvent(reviewRequestUpdatedEvent);
    }

    escalateDocument = async (event: any) => {
        const dialog = document.getElementById("dialog") as Dialog;
        try {
            var accessToken = event.target.dataset.value;
            // ensure that the access token gets refreshed if necessary
            await this.getAccessToken(true);
            //retrieve the reviewRequest
            var reviewRequest = this.reviewRequests[this.reviewRequestsIndex];
            // Log escalation initiation (again, if not already logged in escalate callback)
            logAction({
                timestamp: new Date().toISOString(),
                severity: "INFO",
                userId: this.userId,
                action: "Document escalation initiated",
                additionalInfo: {
                    reviewRequestName: reviewRequest.name,
                    batch_id: reviewRequest.data?.batch_id,
                    envelope_id: reviewRequest.data?.envelope_id,
                    processorType: reviewRequest.data?.processorType,
                    queue: reviewRequest.queue,
                },
            });
            //check if it is the entity extractor (HINT)
            if (reviewRequest.type === "Entity") {
                var pubsubMessage = reviewRequest.data;
                console.log("This is the stringified pubsubMessage:", JSON.stringify(pubsubMessage));
                // publish message to the escalationTopic
                var url_topic = `https://pubsub.googleapis.com/v1/projects/${reviewRequest.projectId}/topics/hint_escalation_queues:publish`;
                await fetchRetry(url_topic, {
                    method: "POST",
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                    body: JSON.stringify({ messages: [{ data: Buffer.from(JSON.stringify(pubsubMessage)).toString("base64") }] }),
                });
                // acknowledge the message from the source subscription
                var url_subscription = `https://pubsub.googleapis.com/v1/${reviewRequest.subscriptionName}:acknowledge`;
                await fetchRetry(url_subscription, {
                    method: "POST",
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                    body: JSON.stringify({ ackIds: [reviewRequest.ackId] }),
                });

            }

            // stop extending acknowledgement deadline for this particular document
            if (this.reviewRequests[this.reviewRequestsIndex].extendAckIdWorker) {
                this.reviewRequests[this.reviewRequestsIndex].extendAckIdWorker.terminate();
            }

            //close the dialog
            dialog.open = false;
            await this.getAccessToken(true);
            this.reviewRequests = this.reviewRequests.filter(
                (_, i) => i !== this.reviewRequestsIndex
            );
            if (this.reviewRequests.length > 0) {
                await this.getAccessToken(true);
                this.reviewRequestsIndex = Math.min(
                    this.reviewRequestsIndex,
                    this.reviewRequests.length - 1
                );
                this.reviewDocument(this.reviewRequests[this.reviewRequestsIndex]);
            } else {
                this.closeReview();
                console.log("REMOVING THE BANNER DUE TO SAVING");
                document.getElementById("documentAgainInHINTBanner").style.display = "none";
            }

        } catch (error) {
            console.log("Error while escalating the document", error);
            logAction({
                timestamp: new Date().toISOString(),
                severity: "ERROR",
                userId: this.userId,
                action: "Document escalation failed",
                additionalInfo: {
                    reviewRequestName: this.reviewRequests[this.reviewRequestsIndex]?.name,
                    batch_id: this.reviewRequests[this.reviewRequestsIndex]?.data?.batch_id,
                    envelope_id: this.reviewRequests[this.reviewRequestsIndex]?.data?.envelope_id,
                    processorType: this.reviewRequests[this.reviewRequestsIndex]?.data?.processorType,
                    queue: this.reviewRequests[this.reviewRequestsIndex]?.queue,
                    error: error,
                },
            });
            dialog.heading = "Eskalation fehlgeschlagen oder abgebrochen.";
            render(
                html`
                    <mwc-button slot="primaryAction" dialogAction="ok"
                    >OK
                    </mwc-button
                    >`,
                dialog
            );
            dialog.open = true;
        }
    };

    /**
     * Check for workflow timeout warnings in recent console logs
     */
    private checkForWorkflowTimeoutWarnings(): boolean {
        // This is a simple heuristic - in a production system you might want
        // to implement a more sophisticated mechanism
        return false; // For now, we'll rely on the fallback mechanism to handle this gracefully
    }

    /**
     * Proceed to the next document after successful save
     */
    private proceedToNextDocument(): void {
        this.reviewRequests = this.reviewRequests.filter(
            (_, i) => i !== this.reviewRequestsIndex
        );
        if (this.reviewRequests.length > 0) {
            this.reviewRequestsIndex = Math.min(
                this.reviewRequestsIndex,
                this.reviewRequests.length - 1
            );
            console.log("REMOVING THE BANNER DUE TO SAVING");
            document.getElementById("documentAgainInHINTBanner")!.style.display = "none";
            this.reviewDocument(this.reviewRequests[this.reviewRequestsIndex]);
        } else {
            this.closeReview();
            console.log("REMOVING THE BANNER DUE TO SAVING");
            document.getElementById("documentAgainInHINTBanner")!.style.display = "none";
        }
    }

    private renderQuarantineProgressDialog(
        dialog: Dialog | null,
        attempt: number,
        maxAttempts: number,
        retrying: boolean = false
    ): void {
        if (!dialog) {
            return;
        }

        dialog.heading = retrying ? "Entfernen wird erneut versucht" : "Aufgabe wird entfernt";
        render(
            html`
                <p>Die Aufgabe wird aus der Bearbeitungsqueue entfernt.</p>
                <mwc-linear-progress indeterminate></mwc-linear-progress>
                <p style="color: #666; font-size: 14px;">
                    Versuch ${attempt} von ${maxAttempts}
                </p>
                <mwc-button slot="primaryAction" disabled>Bitte warten</mwc-button>
            `,
            dialog
        );
        dialog.open = true;
    }

    private renderQuarantineRetryDialog(
        dialog: Dialog | null,
        errorMessage: string
    ): void {
        if (!dialog) {
            return;
        }

        dialog.heading = "Aufgabe konnte nicht entfernt werden";
        render(
            html`
                <p>Die Aufgabe konnte nicht aus der Bearbeitungsqueue entfernt werden.</p>
                <p style="color: #666; font-size: 14px;">
                    ${errorMessage || "Unbekannter Fehler"}
                </p>
                <p>Sie wurde nicht freigegeben und nicht als eskaliert markiert. Bitte versuchen Sie es erneut.</p>
                <mwc-button slot="primaryAction" @click=${(event: Event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    void this.discardTaskAndProceed(dialog);
                }}>Erneut versuchen</mwc-button>
                <mwc-button slot="secondaryAction" dialogAction="close" @click=${() => {
                    dialog.close();
                    this.closeReview();
                }}>Zur Startseite</mwc-button>
            `,
            dialog
        );
        dialog.open = true;
    }

    private waitBeforeQuarantineRetry(attempt: number): Promise<void> {
        return new Promise((resolve) => {
            globalThis.setTimeout(resolve, QUARANTINE_RETRY_DELAY_MS * attempt);
        });
    }

    private buildQuarantinePayload(failureReason: string, failedAt: string) {
        return {
            status: 'escalated',
            assignedTo: null,
            assignedAt: null,
            expiresAt: null,
            failedAt,
            failedBy: this.userId,
            failureStage: 'document_load',
            failureAction: 'operator_discard_after_load_error',
            failureReason
        };
    }

    private async patchQuarantineTask(
        taskDocUrl: string,
        idToken: string,
        updatePayload: ReturnType<HitlReviewDocument["buildQuarantinePayload"]>
    ): Promise<void> {
        const updateResponse = await fetch(taskDocUrl, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(updatePayload)
        });

        if (!updateResponse.ok) {
            const responseText = await updateResponse.text().catch(() => "No response body");
            throw new Error(
                `Quarantine PATCH failed (${updateResponse.status} ${updateResponse.statusText}): ${responseText}`
            );
        }
    }

    private async tryQuarantinePatch(
        taskDocUrl: string,
        idToken: string,
        updatePayload: ReturnType<HitlReviewDocument["buildQuarantinePayload"]>,
        attempt: number
    ): Promise<string | null> {
        try {
            await this.patchQuarantineTask(taskDocUrl, idToken, updatePayload);
            return null;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(
                `Error quarantining unloadable task (attempt ${attempt}/${QUARANTINE_MAX_ATTEMPTS}):`,
                error
            );
            return errorMessage;
        }
    }

    private async retryQuarantinePatch(
        dialog: Dialog | null,
        taskDocUrl: string,
        idToken: string,
        updatePayload: ReturnType<HitlReviewDocument["buildQuarantinePayload"]>
    ): Promise<string | null> {
        let lastErrorMessage = "";
        for (let attempt = 1; attempt <= QUARANTINE_MAX_ATTEMPTS; attempt++) {
            this.renderQuarantineProgressDialog(
                dialog,
                attempt,
                QUARANTINE_MAX_ATTEMPTS,
                attempt > 1
            );

            lastErrorMessage = await this.tryQuarantinePatch(taskDocUrl, idToken, updatePayload, attempt) ?? "";
            if (!lastErrorMessage) {
                return null;
            }

            if (attempt < QUARANTINE_MAX_ATTEMPTS) {
                this.renderQuarantineProgressDialog(
                    dialog,
                    attempt + 1,
                    QUARANTINE_MAX_ATTEMPTS,
                    true
                );
                await this.waitBeforeQuarantineRetry(attempt);
            }
        }
        return lastErrorMessage;
    }

    private async logQuarantineSuccess(
        currentRequest: FirestoreReviewRequest,
        failedAt: string,
        failureReason: string
    ): Promise<void> {
        console.log(`Quarantined unloadable Firestore task ${currentRequest.taskId}`);
        await sendCriticalLog({
            timestamp: failedAt,
            severity: 'ERROR',
            userId: this.userId,
            action: 'Quarantined unloadable Firestore task',
            additionalInfo: {
                taskId: currentRequest.taskId,
                queue: currentRequest.queue,
                envelopeId: currentRequest.envelopeId,
                reviewRequestName: currentRequest.name,
                failureReason
            }
        });
    }

    private async logQuarantineFailure(
        currentRequest: FirestoreReviewRequest,
        failureReason: string,
        errorMessage: string
    ): Promise<void> {
        await sendCriticalLog({
            timestamp: new Date().toISOString(),
            severity: 'ERROR',
            userId: this.userId,
            action: 'Failed to quarantine unloadable Firestore task',
            additionalInfo: {
                taskId: currentRequest.taskId,
                queue: currentRequest.queue,
                envelopeId: currentRequest.envelopeId,
                reviewRequestName: currentRequest.name,
                failureReason,
                error: errorMessage
            }
        });
    }

    private dispatchQuarantineEvent(currentRequest: FirestoreReviewRequest): void {
        const queueUpdateEvent = new CustomEvent("task-completed", {
            bubbles: true,
            detail: {
                queueName: currentRequest.queue,
                taskId: currentRequest.taskId,
                status: 'escalated'
            }
        });
        document.dispatchEvent(queueUpdateEvent);
    }

    private async quarantineFirestoreTask(
        currentRequest: FirestoreReviewRequest,
        dialog: Dialog | null,
        failureReason: string
    ): Promise<boolean> {
        try {
            const failedAt = new Date().toISOString();
            const [, idToken] = await this.getAccessToken(true);
            const collectionName = `hitl_tasks_${currentRequest.queue}`;
            const taskDocUrl = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/${currentRequest.taskId}`;
            const updatePayload = this.buildQuarantinePayload(failureReason, failedAt);
            const errorMessage = await this.retryQuarantinePatch(dialog, taskDocUrl, idToken, updatePayload);

            if (errorMessage) {
                await this.logQuarantineFailure(currentRequest, failureReason, errorMessage);
                this.renderQuarantineRetryDialog(dialog, errorMessage);
                return false;
            }

            await this.logQuarantineSuccess(currentRequest, failedAt, failureReason);
            this.dispatchQuarantineEvent(currentRequest);
            return true;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error("Error preparing unloadable task quarantine:", error);
            await this.logQuarantineFailure(currentRequest, failureReason, errorMessage);
            this.renderQuarantineRetryDialog(dialog, errorMessage);
            return false;
        }
    }

    /**
     * Move an unloadable task out of the normal assignment queue and proceed to the next document.
     */
    private async discardTaskAndProceed(dialog: Dialog | null = document.getElementById("dialog") as Dialog | null): Promise<void> {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        if (!(currentRequest instanceof FirestoreReviewRequest)) {
            this.proceedToNextDocument();
            return;
        }

        const failureReason = this.lastDocumentLoadError || "Document could not be loaded in HITL UI";
        const quarantined = await this.quarantineFirestoreTask(currentRequest, dialog, failureReason);
        if (quarantined) {
            dialog?.close();
            this.proceedToNextDocument();
        }
    }

    // ==================== KEYBOARD SHORTCUT HANDLERS ====================
    // Based on working implementation from second repo

    /**
     * Click the clearing button directly
     */
    private clickClearingButton() {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];
        const processorType = currentRequest instanceof FirestoreReviewRequest
            ? currentRequest.processorType
            : currentRequest.type;

        logAction({
            timestamp: new Date().toISOString(),
            severity: 'INFO',
            userId: this.userId,
            action: 'Send to Clearing (Keyboard Shortcut)',
            additionalInfo: {
                reviewRequestName: currentRequest.name,
                shortcut: 'Alt+8'
            }
        });

        // Try multiple strategies to find and click the clearing/secondary button
        let clearingButton: HTMLButtonElement | null = null;

        if (this.documentViewElement) {
            // Strategy 1: Look for secondary button class
            if (this.documentViewElement.shadowRoot) {
                clearingButton = this.documentViewElement.shadowRoot.querySelector<HTMLButtonElement>('.secondary-button');
            }
            if (!clearingButton) {
                clearingButton = this.documentViewElement.querySelector<HTMLButtonElement>('.secondary-button');
            }

            // Strategy 2: Look for button containing "CLEARING" text
            if (!clearingButton) {
                const allButtons = this.documentViewElement.shadowRoot
                    ? this.documentViewElement.shadowRoot.querySelectorAll('button')
                    : this.documentViewElement.querySelectorAll('button');

                for (const button of allButtons) {
                    if (button.textContent?.includes('CLEARING') ||
                        button.textContent?.includes('Clearing') ||
                        button.getAttribute('label')?.includes('CLEARING')) {
                        clearingButton = button as HTMLButtonElement;
                        break;
                    }
                }
            }

            // Strategy 3: Look for mwc-button with specific attributes
            if (!clearingButton) {
                const mwcButtons = this.documentViewElement.shadowRoot
                    ? this.documentViewElement.shadowRoot.querySelectorAll('mwc-button')
                    : this.documentViewElement.querySelectorAll('mwc-button');

                for (const button of mwcButtons) {
                    if (button.getAttribute('label') === 'CLEARING' ||
                        button.textContent?.includes('CLEARING')) {
                        clearingButton = button as HTMLButtonElement;
                        break;
                    }
                }
            }
        }

        if (clearingButton) {
            clearingButton.click();
        } else {
            // Fallback: Try to trigger the secondaryButtonClick event programmatically
            if (this.documentViewElement && typeof this.documentViewElement.triggerSecondaryAction === 'function') {
                this.documentViewElement.triggerSecondaryAction();
            } else {
                console.error('Clearing button not found and no fallback method available');
            }
        }
    }

    /**
     * Delete Process (Ctrl+Alt+0)
     */
    private deleteProcess() {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        // Only allow delete process in classification mode
        const processorType = currentRequest instanceof FirestoreReviewRequest
            ? currentRequest.processorType
            : currentRequest.type;

        if (processorType !== PROCESSOR_TYPES.CLASSIFICATION && currentRequest.type !== "DAI_COMPLEX_CLASSIFICATION") {
            return;
        }

        logAction({
            timestamp: new Date().toISOString(),
            severity: 'INFO',
            userId: this.userId,
            action: 'Delete Process (Keyboard Shortcut)',
            additionalInfo: {
                reviewRequestName: currentRequest.name,
                shortcut: 'Ctrl+Alt+0'
            }
        });

        const dialog = document.getElementById("dialog") as Dialog;
        if (dialog) {
            dialog.heading = "Prozess löschen";
            render(
                html`<p>Sind Sie sicher, dass Sie diesen Prozess löschen möchten?</p>
                <p><strong>Diese Aktion kann nicht rückgängig gemacht werden.</strong></p>
                <mwc-button
                        slot="secondaryAction"
                        dialogAction="cancel"
                >Abbrechen
                </mwc-button>
                <mwc-button
                        slot="primaryAction"
                        dialogAction="ok"
                        @click=${this.confirmDeleteProcess}
                >Löschen
                </mwc-button>`,
                dialog
            );
            dialog.open = true;
        }
    }

    private confirmDeleteProcess = (): void => {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        try {
            logAction({
                timestamp: new Date().toISOString(),
                severity: 'WARNING',
                userId: this.userId,
                action: 'Process deleted',
                additionalInfo: {
                    reviewRequestName: currentRequest.name
                }
            });

            this.reviewRequests = this.reviewRequests.filter(
                (_, i) => i !== this.reviewRequestsIndex
            );

            const dialog = document.getElementById("dialog") as Dialog;
            if (dialog) {
                dialog.open = false;
            }

            if (this.reviewRequests.length > 0) {
                this.reviewRequestsIndex = Math.min(
                    this.reviewRequestsIndex,
                    this.reviewRequests.length - 1
                );
                this.reviewDocument(this.reviewRequests[this.reviewRequestsIndex]);
            } else {
                this.closeReview();
            }
        } catch (error) {
            console.error('Error deleting process:', error);
        }
    };

    /**
     * Clear All Extracted Fields (Ctrl+Shift+D)
     */
    private clearAllExtractedFields() {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        // Only allow clearing fields in data extraction mode (Entity type)
        const processorType = currentRequest instanceof FirestoreReviewRequest
            ? currentRequest.processorType
            : currentRequest.type;

        if (processorType !== PROCESSOR_TYPES.EXTRACTION && currentRequest.type !== "Entity") {
            return;
        }

        logAction({
            timestamp: new Date().toISOString(),
            severity: 'INFO',
            userId: this.userId,
            action: 'Clear All Extracted Fields (Keyboard Shortcut)',
            additionalInfo: {
                reviewRequestName: currentRequest.name,
                shortcut: 'Ctrl+Shift+D'
            }
        });

        // Find all delete buttons and click them to clear all fields.
        const deleteButtons = document.querySelectorAll('button[aria-label="Quick delete"], button[cfctooltip="delete"]');

        if (deleteButtons.length > 0) {
            deleteButtons.forEach(button => (button as HTMLElement).click());
            this.showTemporaryNotification(`${deleteButtons.length} fields cleared successfully.`, 2000);
        } else {
            this.showTemporaryNotification('Could not find any fields to clear.', 2000);
        }
    }

    /**
     * Exit Without Saving (F4)
     */
    private exitWithoutSaving() {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        // Only allow exit without saving in data extraction mode (Entity type)
        const processorType = currentRequest instanceof FirestoreReviewRequest
            ? currentRequest.processorType
            : currentRequest.type;

        if (processorType !== PROCESSOR_TYPES.EXTRACTION && currentRequest.type !== "Entity") {
            return;
        }

        logAction({
            timestamp: new Date().toISOString(),
            severity: 'INFO',
            userId: this.userId,
            action: 'Exit Without Saving (F4 hotkey)',
            additionalInfo: {
                reviewRequestName: currentRequest.name,
                shortcut: 'F4'
            }
        });

        // Return to main page without saving - same as back button behavior
        this.closeReview();
        // Hide the banner if it was being displayed
        const banner = document.getElementById("documentAgainInHINTBanner");
        if (banner) {
            banner.style.display = "none";
        }
    }

    /**
     * Navigate to Next Field or Complete (Enter)
     */
    private navigateToNextFieldOrComplete() {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        // Only allow field navigation in data extraction mode (Entity type)
        const processorType = currentRequest instanceof FirestoreReviewRequest
            ? currentRequest.processorType
            : currentRequest.type;

        if (processorType !== PROCESSOR_TYPES.EXTRACTION && currentRequest.type !== "Entity") {
            return;
        }

        try {
            // Field navigation only - Enter key no longer triggers save
            const activeElement = document.activeElement;

            if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.closest('mwc-textfield'))) {
                // We're in an input field, try to navigate to next
                const allInputs = Array.from(document.querySelectorAll('input, textarea, mwc-textfield')) as HTMLElement[];
                const currentIndex = allInputs.indexOf(activeElement as HTMLElement);

                if (currentIndex !== -1 && currentIndex < allInputs.length - 1) {
                    // Move to next field
                    logAction({
                        timestamp: new Date().toISOString(),
                        severity: 'INFO',
                        userId: this.userId,
                        action: 'Next Field (Enter hotkey)',
                        additionalInfo: {
                            reviewRequestName: currentRequest.name,
                            fieldIndex: currentIndex + 1
                        }
                    });

                    allInputs[currentIndex + 1].focus();
                } else {
                    // At last field - stay focused, no save action
                    logAction({
                        timestamp: new Date().toISOString(),
                        severity: 'INFO',
                        userId: this.userId,
                        action: 'Last Field Reached (Enter hotkey)',
                        additionalInfo: {
                            reviewRequestName: currentRequest.name,
                            message: 'Use Ctrl+S to save document'
                        }
                    });
                }
            }
            // If not in an input field, do nothing (no accidental saves)
        } catch (error) {
            console.error('Error in navigateToNextFieldOrComplete:', error);
        }
    }


    /**
     * Select classification class by ID (Number keys 0-9)
     * Finds and selects a classification class that starts with the given ID
     *
     * NEW APPROACH: Directly manipulate the <cfc-select> component to update the dropdown
     */
    private selectClassById(classId: string) {
        const currentRequest = this.reviewRequests[this.reviewRequestsIndex];

        // Only work in classification mode
        const processorType = currentRequest instanceof FirestoreReviewRequest
            ? currentRequest.processorType
            : currentRequest.type;

        if (processorType !== PROCESSOR_TYPES.CLASSIFICATION && currentRequest.type !== "DAI_COMPLEX_CLASSIFICATION") {
            return;
        }

        // Find the classification label that starts with this ID
        const classificationLabel = currentRequest.labels.find(
            label => label instanceof ClassificationLabel
        ) as ClassificationLabel | undefined;

        if (!classificationLabel || !classificationLabel.values) {
            console.warn(`No classification labels found for hotkey ${classId}`);
            return;
        }

        // Find the EntityLabel that starts with the given ID
        const matchingLabel = classificationLabel.values.find(
            (value: EntityLabel) => value.label.startsWith(`${classId} - `)
        );

        if (!matchingLabel) {
            console.warn(`No classification class found for ID: ${classId}`);
            this.showTemporaryNotification(`Keine Klasse für ID ${classId} gefunden`, 2000);
            return;
        }

        console.log(`DEBUG selectClassById - Selected class: ${matchingLabel.label}`);

        // Log the selection
        logAction({
            timestamp: new Date().toISOString(),
            severity: 'INFO',
            userId: this.userId,
            action: 'Classification Hotkey Selection',
            additionalInfo: {
                reviewRequestName: currentRequest.name,
                hotkey: classId,
                selectedClass: matchingLabel.label
            }
        });

        // Update the classification label's selectedValue - this is what gets saved!
        // IMPORTANT: Strip the ID prefix before saving to avoid backend KeyError
        // The label format is "ID - ClassName", but backend expects only "ClassName"
        const strippedLabel = (matchingLabel as any).originalDisplayName || matchingLabel.label.replace(/^\d+ - /, '');
        classificationLabel.selectedValue = strippedLabel;
        console.log(`DEBUG - Updated classificationLabel.selectedValue from: "${matchingLabel.label}" to: "${classificationLabel.selectedValue}"`);

        // NEW: Try to update the <cfc-select> (Angular Material) dropdown element directly
        try {
            // Find the cfc-select element (Angular Material component)
            const cfcSelect = document.querySelector('cfc-select[role="combobox"]') as any;

            if (cfcSelect) {
                console.log('DEBUG - Found cfc-select element:', cfcSelect);

                // Strategy 1: Click the dropdown to open it
                cfcSelect.click();
                console.log('DEBUG - Clicked dropdown to open');

                // Wait for dropdown to open and find the matching option
                setTimeout(() => {
                    // Find all mat-option elements that contain the matching text
                    const options = document.querySelectorAll('mat-option');
                    console.log(`DEBUG - Found ${options.length} mat-option elements`);

                    let foundOption: HTMLElement | null = null;
                    for (const option of options) {
                        const optionText = option.querySelector('.mat-option-text');
                        if (optionText && optionText.textContent?.trim() === matchingLabel.label) {
                            foundOption = option as HTMLElement;
                            console.log('DEBUG - Found matching option:', optionText.textContent);
                            break;
                        }
                    }

                    if (foundOption) {
                        // Click the matching option
                        foundOption.click();
                        console.log('DEBUG - Clicked matching option');

                        this.showTemporaryNotification(
                            `✓ Klasse "${matchingLabel.label}" ausgewählt`,
                            3000
                        );
                    } else {
                        console.warn('DEBUG - Could not find matching option in dropdown');

                        // Close the dropdown
                        cfcSelect.click();

                        this.showTemporaryNotification(
                            `✓ Klasse "${matchingLabel.label}" intern ausgewählt`,
                            3000
                        );
                    }
                }, 100); // Wait 100ms for dropdown to open
            } else {
                console.warn('DEBUG - cfc-select element not found, falling back to internal state only');
                this.showTemporaryNotification(
                    `✓ Klasse "${matchingLabel.label}" intern ausgewählt`,
                    3000
                );
            }
        } catch (error) {
            console.error('Error updating cfc-select dropdown:', error);
            this.showTemporaryNotification(
                `✓ Klasse "${matchingLabel.label}" intern ausgewählt`,
                3000
            );
        }
    }

    /**
     * Show digit buffer visual feedback for multi-digit class ID input
     */
    private showDigitBufferFeedback(buffer: string): void {
        // Remove existing feedback if any
        this.hideDigitBufferFeedback();

        // Create feedback element
        const feedback = document.createElement('div');
        feedback.id = 'digit-buffer-feedback';
        feedback.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 135, 70, 0.95);
      color: white;
      padding: 24px 32px;
      border-radius: 8px;
      font-family: 'Roboto', sans-serif;
      font-size: 32px;
      font-weight: 500;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
      z-index: 10001;
      min-width: 100px;
      text-align: center;
      border: 3px solid rgba(255, 255, 255, 0.3);
    `;

        // Add content with current buffer and hint
        feedback.innerHTML = `
      <div style="margin-bottom: 8px;">Klassen-ID:</div>
      <div style="font-size: 48px; font-weight: 700; letter-spacing: 4px;">${buffer}</div>
      <div style="font-size: 14px; margin-top: 12px; opacity: 0.9;">
        Enter zum Bestätigen | Esc zum Abbrechen
      </div>
    `;

        // Add to document
        document.body.appendChild(feedback);
    }

    /**
     * Hide digit buffer visual feedback
     */
    private hideDigitBufferFeedback(): void {
        const existing = document.getElementById('digit-buffer-feedback');
        if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }
    }

    /**
     * Show temporary notification to user
     */
    private showTemporaryNotification(message: string, duration: number = 3000) {
        // Create notification element
        const notification = document.createElement('div');
        notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #BE153F !important;
      color: white;
      padding: 16px 24px;
      border-radius: 4px;
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease;
    `;
        notification.textContent = message;

        // Add to document
        document.body.appendChild(notification);

        // Show with animation
        setTimeout(() => {
            notification.style.opacity = '1';
            notification.style.transform = 'translateX(0)';
        }, 10);

        // Hide and remove after duration
        setTimeout(() => {
            notification.style.opacity = '0';
            notification.style.transform = 'translateX(100%)';

            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 300);
        }, duration);
    }

    override disconnectedCallback() {
        hitlKeyboardShortcuts.deactivate();
        super.disconnectedCallback();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "hitl-review-document": HitlReviewDocument;
    }
}
